import { ApiTimeoutError, JFETCH_DEFAULT_TIMEOUT_MS } from "./resourceState";

export { ApiTimeoutError, isTimeoutError } from "./resourceState";
export type { ResourceState, ResourceStatus } from "./resourceState";

const SESSION_TOKEN_STORAGE_KEY = "simo.sessionToken";
const PROXY_BASIC_AUTH_STORAGE_KEY = "simo.proxyBasicAuth";
const OWNER_SESSION_ACTIVE_STORAGE_KEY = "simo.ownerSessionActive";
export const OWNER_SESSION_INVALIDATED_EVENT = "simo-owner-session-invalidated";

export type OwnerSessionInvalidatedReason = "missing_session" | "invalid_session" | "unauthorized";

export interface OwnerSessionInvalidatedDetail {
  reason: OwnerSessionInvalidatedReason;
  path: string;
  requestId: string | null;
  message: string;
}

export function getSessionToken(): string {
  return "";
}

export function markOwnerSessionActive(): void {
  try {
    localStorage.removeItem(SESSION_TOKEN_STORAGE_KEY);
    localStorage.removeItem(PROXY_BASIC_AUTH_STORAGE_KEY);
    localStorage.setItem(OWNER_SESSION_ACTIVE_STORAGE_KEY, "1");
  } catch {
    /* localStorage unavailable — App verifies via cookie-backed session endpoint */
  }
}

export function setSessionToken(_token: string): void {
  markOwnerSessionActive();
}

export function clearSessionToken(): void {
  try {
    localStorage.removeItem(SESSION_TOKEN_STORAGE_KEY);
    localStorage.removeItem(OWNER_SESSION_ACTIVE_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function hasSessionToken(): boolean {
  try {
    return localStorage.getItem(OWNER_SESSION_ACTIVE_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

export function getProxyBasicAuthHeader(): string {
  try {
    localStorage.removeItem(PROXY_BASIC_AUTH_STORAGE_KEY);
  } catch {
    /* ignore */
  }
  return "";
}

export function setProxyBasicAuth(_email: string, _password: string): void {
  clearProxyBasicAuth();
}

export function clearProxyBasicAuth(): void {
  try {
    localStorage.removeItem(PROXY_BASIC_AUTH_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

export function clearOwnerBrowserAuth(): void {
  clearSessionToken();
  clearProxyBasicAuth();
}

export function hasOwnerBrowserAuth(): boolean {
  return hasSessionToken();
}

export class ApiRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly statusText: string,
    message: string,
    public readonly requestId: string | null,
    public readonly errorCode: string | null = null,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

function parseRequestId(headers: Headers, bodyText: string): string | null {
  const headerValue =
    headers.get("x-request-id") ??
    headers.get("x-simot-api-request-id") ??
    headers.get("x-simo-api-request-id");
  if (headerValue) return headerValue;
  try {
    const parsed = JSON.parse(bodyText) as { requestId?: unknown };
    return typeof parsed.requestId === "string" && parsed.requestId.length > 0
      ? parsed.requestId
      : null;
  } catch {
    return null;
  }
}

function parseErrorCode(bodyText: string): string | null {
  try {
    // Agent-Exchange-Antworten tragen ihren fachlichen Code in `reason`
    // (sendFailure), Auth-Routen in `error`. Beide lesen — sonst bliebe z.B.
    // `trust_root_required` unsichtbar und die UI zeige nur den generischen 403.
    const parsed = JSON.parse(bodyText) as { error?: unknown; reason?: unknown };
    if (typeof parsed.error === "string") return parsed.error;
    return typeof parsed.reason === "string" ? parsed.reason : null;
  } catch {
    return null;
  }
}

function sessionInvalidationReason(path: string, status: number, bodyText: string): OwnerSessionInvalidatedReason | null {
  if (status !== 401 || path === "/api/auth/login") return null;
  const errorCode = parseErrorCode(bodyText);
  if (errorCode === "missing_session" || errorCode === "invalid_session") return errorCode;
  //_OWNER-ACCESS-LOGIN-INCIDENT-1: Ein generisches 401 ("unauthorized",
  //_API-Gate/Bearer/Proxy) ist KEINE Session-Invalidierung — die Session kann
  //_gueltig sein, waehrend eine Infrastruktur-Grenze ablehnt. Frueher fuehrte
  //_das zum Login-Redirect-Loop ("API-Auth wurde nicht akzeptiert" trotz
  //_korrektem Login). Solche Fehler bleiben API-Fehler mit klarer Meldung,
  //_ohne Session-Reset und ohne Login-Umleitung.
  return null;
}

function notifyOwnerSessionInvalidated(detail: OwnerSessionInvalidatedDetail): void {
  clearOwnerBrowserAuth();
  if (typeof window === "undefined") return;
  const event = typeof CustomEvent === "function"
    ? new CustomEvent<OwnerSessionInvalidatedDetail>(OWNER_SESSION_INVALIDATED_EVENT, { detail })
    : Object.assign(new Event(OWNER_SESSION_INVALIDATED_EVENT), { detail });
  window.dispatchEvent(event);
}

function ownerAuthErrorMessage(path: string, status: number, statusText: string, bodyText: string, requestId: string | null): string {
  const errorCode = parseErrorCode(bodyText);
  const prefix =
    status === 401 && (errorCode === "missing_session" || errorCode === "invalid_session")
      ? `Owner-Session fehlt oder ist abgelaufen. Bitte neu anmelden und danach Live-Daten erneut laden. Endpoint: ${path}.`
      : status === 401 && errorCode === "unauthorized"
        ? `API-Zugriff nicht freigegeben. Der Server hat den Request vor der Owner-Session-Pruefung abgelehnt. Endpoint: ${path}.`
        : status === 401
          ? `Owner Auth nicht akzeptiert. Bitte neu anmelden und danach erneut versuchen. Endpoint: ${path}.`
          : status === 403 && (errorCode === "learning_review_forbidden" || errorCode === "insufficient_role")
            ? `Owner-Freigabe erforderlich. Deine Session ist vorhanden, aber diese Live-Daten brauchen Owner/Admin-Rechte. Endpoint: ${path}.`
            : status === 403 && errorCode === "step_up_password_invalid"
              ? `Passwort nicht bestätigt. Bitte erneut versuchen. Endpoint: ${path}.`
            : status === 403 && errorCode === "trust_root_required"
              ? `Owner-Bestätigung erforderlich: Diese Entscheidung verschiebt Autorität und verlangt die SEC-2-Trust-Wurzel. Bitte Owner-Passwort bestätigen und erneut freigeben. Endpoint: ${path}.`
            : status === 403
              ? `Owner Auth nicht berechtigt: Policy verweigert diese Aktion. Endpoint: ${path}.`
              : `${status} ${statusText}: ${bodyText}`;
  return requestId ? `${prefix} Request-ID ${requestId}` : prefix;
}

function buildOwnerApiError(path: string, res: Response, bodyText: string): Error {
  const requestId = parseRequestId(res.headers, bodyText);
  const message = ownerAuthErrorMessage(path, res.status, res.statusText, bodyText, requestId);
  const invalidationReason = sessionInvalidationReason(path, res.status, bodyText);
  if (invalidationReason) {
    notifyOwnerSessionInvalidated({ reason: invalidationReason, path, requestId, message });
  }
  return new ApiRequestError(res.status, res.statusText, message, requestId, parseErrorCode(bodyText));
}

export interface WebPushConfigView {
  configured: boolean;
  publicKey?: string;
  reason?: string;
}

export interface WebPushSubscriptionView {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

/**
 * LOADING-TRUTH-FIX (S3): jfetch hat jetzt ein hartes Timeout (default
 * 15000 ms, pro Aufruf ueberschreibbar). Bei Timeout wird ein klassifizierter
 * ApiTimeoutError (code=TIMEOUT) geworfen — fetch settle damit IMMER, statt
 * endlos zu haengen (vorher: endloses "Lade…", weil abhaengige Server-Routen
 * an externen Probes ohne Timeout hingen). Ein vom Aufrufer mitgegebenes
 * init.signal wird respektiert (kombiniert).
 */
export async function jfetch<T>(
  path: string,
  init?: RequestInit,
  opts?: { timeoutMs?: number },
): Promise<T> {
  const hasBody = init?.body != null;
  const headers: Record<string, string> = { ...(init?.headers as Record<string, string> | undefined ?? {}) };
  if (hasBody && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }
  const timeoutMs = opts?.timeoutMs ?? JFETCH_DEFAULT_TIMEOUT_MS;
  const timeoutSignal = AbortSignal.timeout(timeoutMs);
  const signal = init?.signal
    ? (typeof AbortSignal.any === "function"
        ? AbortSignal.any([init.signal, timeoutSignal])
        : init.signal)
    : timeoutSignal;
  // Eigener Race-Deckel: settle ist NICHT davon abhaengig, ob die fetch-
  // Implementierung das Signal rechtzeitig beobachtet (aeltere Umgebungen
  // ignorieren es) — jfetch wirft garantiert nach timeoutMs.
  let timeoutTimer: ReturnType<typeof setTimeout> | undefined;
  const timeoutRace = new Promise<never>((_resolve, reject) => {
    timeoutTimer = setTimeout(() => reject(new ApiTimeoutError(path, timeoutMs)), timeoutMs);
  });
  let res: Response;
  try {
    res = await Promise.race([fetch(path, { ...init, headers, credentials: "same-origin", signal }), timeoutRace]);
  } catch (err) {
    if (err instanceof ApiTimeoutError) throw err;
    if (timeoutSignal.aborted) {
      throw new ApiTimeoutError(path, timeoutMs);
    }
    throw err;
  } finally {
    if (timeoutTimer !== undefined) clearTimeout(timeoutTimer);
  }
  if (!res.ok) {
    const text = await res.text();
    throw buildOwnerApiError(path, res, text);
  }
  return (await res.json()) as T;
}

/**
 * Batch-Advance mit ehrlichen Per-Mission-Ergebnissen. Bewusst eigener Fetch
 * statt jfetch: HTTP 207 (Teil-Fehler) und 409 mit results-Feld sind hier
 * KEINE Ausnahmen, die verworfen werden duerfen — genau dann braucht die UI
 * die je-Mission-Wahrheit. Alles andere uebernimmt der normale Fehlerpfad.
 */
export async function authorityAdvanceBatchEhrlich(reason: string, gateId: string): Promise<BatchAdvanceResponse> {
  const path = "/api/agent-exchange/missions/authority/advance-batch";
  const res = await fetch(path, {
    method: "POST",
    headers: { "Content-Type": "application/json", "x-simo-exchange-surface": "simo" },
    credentials: "same-origin",
    // S0.1: Der Batch bindet exakt den Snapshot des benannten Gates.
    body: JSON.stringify({ reason, gateId }),
  });
  const text = await res.text();
  let body: BatchAdvanceResponse | null = null;
  try {
    body = JSON.parse(text) as BatchAdvanceResponse;
  } catch {
    body = null;
  }
  if (res.ok || res.status === 207 || (res.status === 409 && body && Array.isArray(body.results))) {
    if (body) return body;
  }
  throw buildOwnerApiError(path, res, text);
}

export type BrainExportSection = "memory" | "learning_proposals" | "decision_logs";
export type BrainExportMode = "redacted" | "full";

export type AgentBrainScopeSpace = "general" | "private" | "business" | "project" | "people" | "decision" | "evidence" | "temporary";
export type AgentBrainScopeSensitivity = "normal" | "private" | "sensitive" | "secret";

export interface AgentBrainScopeView {
  allowedSpaces: AgentBrainScopeSpace[];
  allowedKinds: string[];
  maxSensitivity: AgentBrainScopeSensitivity;
  isDefault: boolean;
  updatedBy?: string | null;
  updatedAt?: string | null;
}

export interface AgentBrainScopeEntryView {
  role: { id: string; name: string };
  scope: AgentBrainScopeView;
}

export type AutonomousWorkQueuePriority = "low" | "normal" | "high" | "urgent";
export type AutonomousWorkQueueStatus = "queued" | "in_progress" | "blocked" | "paused" | "done" | "archived";
export type AutonomousWorkQueueApprovalMode = "allow" | "ask" | "read_only" | "block" | "session_allow";

export interface AutonomousWorkQueueItemView {
  id: string;
  source: string;
  title: string;
  goal: string;
  productScope: string;
  priority: AutonomousWorkQueuePriority;
  status: AutonomousWorkQueueStatus;
  assignedAgent: string;
  requiredCapabilitiesJson?: string;
  approvalsNeededJson?: string;
  evidenceJson?: string;
  dueAt?: string | null;
  reviewAt?: string | null;
  blockedReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface BrainExportDownloadOptions {
  space?: string;
  ownerId?: string;
  userId?: string;
  keys?: string[];
  sections?: BrainExportSection[];
  mode?: BrainExportMode;
  confirmFullExport?: boolean;
}

export interface BrainRestoreValidateResult {
  ok: true;
  requestId: string;
  sourceRequestId: string | null;
  canApplyWithoutFullExport: boolean;
  mutations: 0;
  counts: {
    memory: number;
    learningProposals: number;
    decisionLogs: number;
  };
  warning: string;
}

export interface RoutineView {
  id: string;
  label: string;
  triggerType: "schedule" | "event" | "threshold";
  triggerConfig: Record<string, unknown>;
  prompt: string;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface RoutineRunView {
  id: number;
  routineId: string;
  triggeredBy: "manual" | "scheduler";
  triggeredAt: string;
  result: "ok" | "error";
  text: string;
}

export interface AgentOsLoopExecutionReceipt {
  runId: string;
  loopId: string;
  status: "completed" | "failed" | "awaiting_approval";
  reasonCode: string | null;
  iterations: number;
  tokenUsage: number;
  lastCheckpointRef: string | null;
  recoveredFromRunId: string | null;
  resumedFromRunId: string | null;
  approvalId: string | null;
  executionMode: "dry_run" | "execute";
}

function brainExportUrl(options: BrainExportDownloadOptions = {}): string {
  const params = new URLSearchParams();
  params.set("space", options.space ?? "all");
  params.set("include", (options.sections?.length ? options.sections : ["memory", "learning_proposals", "decision_logs"]).join(","));
  params.set("mode", options.mode ?? "redacted");
  if (options.ownerId) params.set("ownerId", options.ownerId);
  if (options.userId) params.set("userId", options.userId);
  if (options.keys?.length) params.set("keys", options.keys.join(","));
  if (options.mode === "full" && options.confirmFullExport) params.set("confirmFullExport", "owner-admin");
  return `/api/memory/export?${params.toString()}`;
}

function filenameFromContentDisposition(value: string | null): string {
  const match = value?.match(/filename="?([^";]+)"?/i);
  return match?.[1] ?? `simo-brain-export-${new Date().toISOString().slice(0, 10)}.json`;
}

async function downloadJson(path: string): Promise<void> {
  const headers: Record<string, string> = {};
  const res = await fetch(path, { headers, credentials: "same-origin" });
  if (!res.ok) {
    const text = await res.text();
    throw buildOwnerApiError(path, res, text);
  }
  const blob = await res.blob();
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = filenameFromContentDisposition(res.headers.get("content-disposition"));
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(href);
}

export interface SimoSessionUser {
  id: string;
  email: string;
  displayName: string | null;
  roleId: string;
  status: string;
}

export interface SimoLoginResult {
  ok: boolean;
  sessionToken?: string;
  expiresAt?: string;
  user?: SimoSessionUser;
  error?: string;
}

export interface SimoPasswordChangeResult {
  ok: boolean;
  error?: string;
}

export interface SimoPasswordResetRequestResult {
  ok: boolean;
  delivery?: "owner_recovery_required";
  error?: string;
}

export interface OwnerAuthStatusView {
  apiReachable: boolean;
  session: {
    authenticated: true;
    userId: string;
    roleId: string;
  };
  ownerAuth: {
    configured: boolean;
    keyId: string | null;
    fingerprint: string | null;
    rotationRequired: boolean;
  };
}

export interface SecretRecordView {
  key: string;
  label: string;
  connector?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PermissionItem {
  id: string;
  connector: string;
  kind: string;
  label: string;
  description: string;
  defaultMode: "off" | "draft" | "suggest" | "auto";
  mode: "off" | "draft" | "suggest" | "auto";
}

export interface AuditEntryView {
  id: number;
  timestamp: string;
  actor: "user" | "jarvis" | "system";
  connector?: string | null;
  actionId?: string | null;
  mode?: string | null;
  result: "ok" | "blocked" | "draft" | "pending" | "error";
  summary: string;
  detailsJson?: string | null;
  hash: string;
  prevHash: string;
}

export interface ApprovalView {
  id: string;
  runId: string;
  conversationId: string;
  toolId: string;
  capability: string;
  mode: "draft" | "suggest";
  riskLevel: "read" | "draft" | "write" | "destructive";
  summary: string;
  payloadPreviewJson: string;
  status: "pending" | "approved" | "rejected" | "expired" | "cancelled";
  createdAt: string;
  expiresAt: string;
}

export interface ApprovalInboxItemView {
  id: string;
  runId: string;
  requestId: string;
  conversationId: string | null;
  toolId: string;
  capability: string;
  mode: "draft" | "suggest";
  riskLevel: "read" | "draft" | "write" | "destructive";
  summary: string;
  payloadPreviewJson: string;
  status: "pending" | "approved" | "rejected" | "expired" | "cancelled";
  createdAt: string;
  expiresAt: string;
  decidedAt: string | null;
  auditId: number | null;
  evidenceRef: string;
  safeActions: Array<"open_details" | "show_evidence" | "approve_status" | "reject_status" | "cancel_status" | "expire_status">;
  blockedActions: Array<"approve_and_execute" | "connector_write" | "shell" | "git" | "deploy">;
}

export interface ApprovalInboxDecisionView {
  ok: boolean;
  approval: {
    id: string;
    status: ApprovalInboxItemView["status"];
    requestId: string;
    evidenceRef: string;
    auditId: number | null;
    toolId: string;
    capability: string;
    mode: ApprovalInboxItemView["mode"];
    riskLevel: ApprovalInboxItemView["riskLevel"];
    decidedAt: string | null;
    blockedActions: ApprovalInboxItemView["blockedActions"];
  };
  actionGates: ApprovalInboxView["actionGates"];
}

export interface ApprovalInboxView {
  statusModel: {
    states: ApprovalInboxItemView["status"][];
    terminalStates: ApprovalInboxItemView["status"][];
  };
  actionGates: {
    canApprove: boolean;
    canReject: boolean;
    canCancel: boolean;
    canExpire: boolean;
    canExecute: boolean;
    connectorWrites: boolean;
    shellGitDeploy: boolean;
    autoExecute: boolean;
    statusOnly: boolean;
    requiresOwnerSession: boolean;
    allowedRoles: string[];
    evidenceRequired: boolean;
  };
  items: ApprovalInboxItemView[];
}

export interface ConnectorWriteLiveEvidenceView {
  ok: boolean;
  status: "proven" | "missing";
  actionId: "gmail.write.draft";
  executionProven: boolean;
  liveOwnerExecutionClaimAllowed: boolean;
  required: {
    resultStatus: "ok";
    actionId: "gmail.write.draft";
    capability: "gmail.write.execute";
    approvalIdRequired: true;
    auditIdRequired: true;
    providerReadbackConfirmed: true;
    noSend: true;
  };
  latest: null | {
    id: number;
    runId: string | null;
    approvalId: string | null;
    auditId: number | null;
    resultStatus: string;
    summary: string | null;
    createdAt: string;
    readback: null | {
      confirmed: boolean;
      draftId?: string;
      messageId?: string;
      threadId?: string;
    };
  };
  counts: {
    scanned: number;
    ok: number;
    confirmedReadback: number;
  };
  boundary: {
    mode: "read_only_governed_connector_write_evidence";
    providerCall: false;
    connectorWrite: false;
    secretValueExposed: false;
    redactedInputReturned: false;
    shellGitDeploy: false;
    deploy: false;
  };
}

export interface ConnectorWriteStoredExecutionView {
  ok: boolean;
  status?: "executed";
  requestId: string;
  executionAllowed: boolean;
  sideEffects?: boolean;
  connectorWrites?: boolean;
  shellGitDeploy?: false;
  deploy?: false;
  reason?: string;
  evidence?: {
    id: number;
    auditId: number;
    approvalId: string;
    runId: string | null;
  };
  output?: {
    actionId: "gmail.write.draft";
    connector: "gmail";
    providerResult: { id: string; threadId?: string };
    providerReadback: { confirmed: boolean; draftId?: string; messageId?: string; threadId?: string };
    redactedInputReturned: false;
  };
  boundary?: {
    mode: "stored_approval_connector_write_execution";
    executionAllowed: true;
    executeEndpoint: "/api/connectors/write-approval/:id/execute-stored";
    policyId: string;
    rendererSuppliedInputAccepted: false;
    storedPayloadExecution: true;
    redactedInputReturned: false;
    shellGitDeploy: false;
    deploy: false;
  };
}

export interface ConnectorWriteStoredExecutionReadinessView {
  ok: boolean;
  status?: "ready" | "not_ready";
  reason?: string;
  approvalId: string;
  runId: string | null;
  toolId: string | null;
  actionId: string | null;
  approvalStatus: ApprovalInboxItemView["status"] | null;
  riskLevel: ApprovalInboxItemView["riskLevel"] | null;
  mode: ApprovalInboxItemView["mode"] | null;
  policyId: string | null;
  canExecuteStored: boolean;
  executionAllowed: false;
  stepUpRequired: boolean;
  stepUpSatisfied: boolean;
  boundary: {
    mode: "read_only_stored_connector_write_execution_readiness";
    providerCall: false;
    connectorWrite: false;
    executionAttempted: false;
    rendererSuppliedInputAccepted?: false;
    storedPayloadExecution?: false;
    redactedInputReturned: false;
    payloadPreviewReturned: false;
    summaryReturned: false;
    secretValueExposed: false;
    shellGitDeploy?: false;
    deploy?: false;
  };
}

export interface AgentRegistryView {
  registryMode: "read_only_foundation";
  statusModel: { states: Array<"idle" | "active" | "blocked" | "error" | "disabled"> };
  policyModel?: {
    agentToTeam: boolean;
    agentToCapabilities: boolean;
    capabilityToPolicyGate: boolean;
    policyGateToApprovalRequirement: boolean;
  };
  routingModel?: {
    orchestratorToTeamLead: boolean;
    teamLeadToSpecialist: boolean;
    policyGateRequired: boolean;
    dryRunOnly: boolean;
    executionAllowed: boolean;
  };
  reviewModel?: {
    reviewerAgents: boolean;
    escalationRequired: boolean;
    ownerDecisionRequired: boolean;
    executionAllowed: boolean;
  };
  actionGates: {
    canStartRun: boolean;
    canInvokeTool: boolean;
    canStartRunnerJob: boolean;
    canApprove: boolean;
    canReject: boolean;
    connectorWrites: boolean;
    shellGitDeploy: boolean;
    autoExecute: boolean;
  };
  policyBoundaries: {
    noAgentExecution: boolean;
    noToolExecution: boolean;
    noRunnerJobs: boolean;
    noApprovalActions: boolean;
  };
  teams: Array<{
    id: string;
    label: string;
    agentIds: string[];
    capabilities: string[];
  }>;
  taskRouting?: {
    mode: "read_only_routing_foundation";
    executionAllowed: boolean;
    routeStartAllowed: boolean;
    routeMutationAllowed: boolean;
    routes: Array<{
      id: string;
      label: string;
      taskType: string;
      orchestratorAgentId: string;
      teamLeadAgentId: string;
      specialistAgentIds: string[];
      requiredCapabilities: string[];
      policyGate: string;
      approvalRequired: boolean;
      blocked: boolean;
      executionAllowed: boolean;
    }>;
  };
  reviewAndEscalation?: {
    mode: "read_only_review_escalation";
    executionAllowed: boolean;
    escalationMutationAllowed: boolean;
    decisions: Array<{
      id: string;
      routeId: string;
      reviewerAgentIds: string[];
      escalationTarget: string;
      decisionRequired: boolean;
      requiredDecisionBy: string;
      policyGate: string;
      approvalRequired: boolean;
      blocked: boolean;
      executionAllowed: boolean;
    }>;
  };
  agents: Array<{
    id: string;
    name: string;
    purpose: string;
    enabled: boolean;
    isBuiltin: boolean;
    type: "builtin" | "custom";
    teamIds: string[];
    assistantModes: string[];
    toolGroups: string[];
    capabilities: string[];
    policyBoundaries: string[];
    highestRiskLevel: string;
    accessLevel: string;
    permissionFloor: "none" | "draft" | "read_only";
    executionState: "idle" | "active" | "blocked" | "error" | "disabled";
    canExecute: boolean;
  }>;
  activityTimeline?: {
    mode: "read_only_activity_timeline";
    executionAllowed: boolean;
    items: Array<{
      id: string;
      actorAgentId: string;
      verb: string;
      routeId: string;
      requestId: string;
      evidenceRef: string;
      policyGate: string;
      blocked: boolean;
    }>;
  };
  structuredLearningOverlay?: {
    mode: "approved_structured_learning_overlay";
    source: string;
    runtimeMutation: boolean;
    mutationAllowed: boolean;
    count: number;
    items: Array<{
      key: string;
      proposalKind: string;
      source: string;
      reviewStatus: string;
      injectPolicy: string;
      appliedAs: string;
      runtimeMutation: boolean;
      changeId: string;
      label: string;
      gate: string;
      evidenceRef: string;
      updatedAt: string;
    }>;
  };
  projectTemplates?: {
    schema: string;
    mode: "read_only_templates_and_skills";
    gates: {
      executionAllowed: boolean;
      autoActivateSkills: boolean;
      unreviewedSkillsAllowed: boolean;
      shellGitDeployAllowed: boolean;
      connectorWritesAllowed: boolean;
      secretAccessAllowed: boolean;
      requiresReviewedSkillBundles: boolean;
    };
    templates: Array<{
      id: string;
      name: string;
      routeId: string;
      purpose: string;
      riskLevel: "read" | "write" | "destructive";
      workflow: string;
      skillBundleIds: string[];
      requiredEvidence: string[];
      gates: {
        executionAllowed: boolean;
        autoActivateSkills: boolean;
        unreviewedSkillsAllowed: boolean;
        requiresOwnerApproval: boolean;
        shellGitDeployAllowed: boolean;
        connectorWritesAllowed: boolean;
        secretAccessAllowed: boolean;
        branchPattern: string | null;
      };
    }>;
    skillBundles?: Array<{
      id: string;
      name: string;
      purpose: string;
      source: string;
      reviewStatus: "approved" | "conditional" | "blocked";
      riskLevel: "read" | "write";
      requiresReview: boolean;
      autoActivate: boolean;
      capabilities: string[];
      workflows: string[];
      skillRefs: string[];
    }>;
  };
}

export interface AgentOsRegistryRecordView extends Record<string, unknown> {
  id: string;
  canonicalHash: string;
  mutationBaseHash: string | null;
  mutationSupported: boolean;
  mutationMode: "bound" | "bootstrap" | null;
  provenance: {
    canonical: { status: "bound" | "missing" | "unavailable"; repository: string | null; revision: string | null; sourceRef: string | null };
    runtime: { status: "projected" | "observed" | "not_projected"; sourceRef: string | null };
    joinStatus: "matched" | "drifted" | "canonical_only" | "runtime_only" | "bootstrap_ready" | "unknown";
  };
  version: { schema: "it-simo.agent-os-registry-record.v2"; canonical: string | null; runtime: string | null; drift: "in_sync" | "drifted" | "canonical_only" | "runtime_only" | "bootstrap_ready" | "unknown" };
}

export type AgentOsRegistryControlStatus = "live_control_ready" | "guided_builder_ready" | "draft_lane_ready" | "proposal_only" | "read_only" | "unavailable";
export interface AgentOsRegistryControlRowView {
  id: string;
  label: string;
  status: AgentOsRegistryControlStatus;
  mutationLane: "agent_os_draft" | "learning_proposal" | "owner_device_route" | "read_only_projection" | "none";
  builder: string | null;
  runtimeSource: string | null;
  draftEntityType: AgentOsDraftEntityType | null;
  recordCount: number;
  mutationReadyCount: number;
  supportsUnifiedMutationLane: boolean;
  writeBoundary: "unified_change_lane" | "guided_builder_without_direct_lane" | "proposal_only" | "read_only_attestation" | "not_yet_supported";
  reason: string;
  evidence: string[];
}

export interface AgentOsStudioReadModelView {
  schema: "it-simo.agent-os-studio-read-model.v2";
  sourceOfTruth: "Git/Forgejo canonical truth + SIMO runtime projection";
  registryRevision: { repository: string; revision: string | null; branch: string | null; status: "bound" | "unavailable"; bindingMethod: "git_content" | "artifact_manifest" | null; bindingReason: string | null; manifestHash: string | null };
  snapshotHash: string;
  sections: Record<"agents" | "skills" | "tools" | "policies" | "routines" | "loops" | "harnesses" | "brain", { status: "available" | "empty" | "unavailable"; count: number }>;
  agents: AgentOsRegistryRecordView[];
  subagents: AgentOsRegistryRecordView[];
  skills: AgentOsRegistryRecordView[];
  hooks: AgentOsRegistryRecordView[];
  tools: AgentOsRegistryRecordView[];
  routines: AgentOsRegistryRecordView[];
  loops: AgentOsRegistryRecordView[];
  harnesses: AgentOsRegistryRecordView[];
  harnessUsage: AgentOsRegistryRecordView[];
  policies: AgentOsRegistryRecordView[];
  brain: AgentOsRegistryRecordView | null;
  brainEntries: AgentOsRegistryRecordView[];
  learning: AgentOsRegistryRecordView[];
  providers: AgentOsRegistryRecordView[];
  hosts: AgentOsRegistryRecordView[];
  runs: AgentOsRegistryRecordView[];
  registryControl: AgentOsRegistryControlRowView[];
  summary: {
    agents: number;
    subagents: number;
    skills: number;
    tools: number;
    routines: number;
    activeRuns: number;
    pendingLearning: number;
  };
}

export interface OwnerMissionView {
  schema: "it-simo.owner-mission-control.v1";
  runId: string;
  conversationId: string;
  goal: string;
  phase: "executing" | "paused" | "awaiting_approval" | "completed" | "cancelled" | "failed";
  status: "running" | "awaiting_approval" | "paused" | "done" | "cancelled" | "error";
  childRunId: string | null;
  children: Array<{
    runId: string;
    parentRunId: string;
    phase: string;
    status: string;
    model: string | null;
    provider: string | null;
    eventCount: number;
    providerEventsObserved: boolean;
    toolEventsObserved: boolean;
  }>;
  approvalIds: string[];
  controls: { canPause: boolean; canResume: boolean; canStop: boolean };
  eventCount: number;
  latestEventType: string | null;
  queue: { workItemId: string; missionId: string; queueState: string } | null;
  redactionStatus: "secrets-omitted";
}

export type BrainLearningSpace = "general" | "private" | "business" | "project" | "people" | "decision" | "evidence" | "temporary";
export type BrainLearningReleaseState = "canary_active" | "stable_active" | "superseded" | "rolled_back";
export interface BrainLearningStudioLessonView {
  id: string;
  status: string;
  confidence: number;
  hitCount: number;
  createdAt: string;
  sourceRef: string;
}
export interface BrainLearningReleaseView {
  id: string;
  lineageKey: string;
  version: number;
  lessonRef: string;
  proposalRef: string | null;
  previousReleaseRef: string | null;
  state: BrainLearningReleaseState;
  contentRef: string;
  baselineRuntimeRef: string;
  space: BrainLearningSpace;
  targetRoleId: string | null;
  canaryRunRef: string | null;
  expiresAt: string;
  createdBy: string;
  createdAt: string;
  promotedAt: string | null;
  rolledBackAt: string | null;
  rollbackRuntimeRef: string | null;
}
export interface BrainLearningStudioView {
  schema: "it-simo.brain-learning-studio.v1";
  space: BrainLearningSpace;
  snapshotRef: string;
  sourceOfTruth: { canonical: string; acceptedOnly: true };
  counts: { lessons: number; releases: number; decisions: number; graphNodes: number; graphEdges: number };
  lessons: BrainLearningStudioLessonView[];
  releases: BrainLearningReleaseView[];
  graph: {
    nodes: Array<{ id: string; kind: string; displayLabel: string; status: string; space: string; sourceRef: string }>;
    edges: Array<{ id: string; from: string; to: string; kind: string; observed: true; evidenceRefs: string[] }>;
  };
  impact: Array<{
    id: string;
    kind: "lesson" | "release" | "runtime_projection";
    label: string;
    state: string;
    usedBy: string[];
    injectedInto: string[];
    nextRunInjection: "accepted_runtime" | "one_run_canary_candidate" | "not_injected";
    rollbackImpact: string[];
    deprecationImpact: string[];
    purgeImpact: { status: "blocked" | "safe_after_rollback" | "requires_owner_review"; blockers: string[] };
    evidenceRefs: string[];
  }>;
  injectionPreview: Array<{
    product: "simo" | "simot";
    routeId: string;
    roleId: string | null;
    nextRunWouldInject: boolean;
    stableReleaseRefs: string[];
    canaryReleaseRefs: string[];
    selectionRef: string;
    rollbackWouldRemoveRefs: string[];
  }>;
  decisions: Array<{ id: string; timestamp: string; actionId: string; result: string; evidenceRef: string }>;
}

export type AgentOsDraftEntityType = "agent" | "skill" | "tool" | "routine" | "loop" | "harness" | "policy" | "brain" | "learning" | "provider";
export type AgentOsSkillWorkflow = "autonomous" | "security" | "visual" | "deploy" | "business";
export interface AgentOsSkillTestReceipt {
  status: "completed";
  runId: string;
  skillId: string;
  skillVersion: string;
  workflow: AgentOsSkillWorkflow;
  protocolRef: string;
  brainContextPacketRef: string;
  capabilityEnvelopeRef: string;
  ledgerEventIds: string[];
  externalWritesPerformed: false;
  providerCalls: 0;
  toolCalls: 0;
  connectorWrites: 0;
}
export interface AgentOsToolPolicyTestReceipt {
  toolId: string;
  actionId: string;
  mode: "off" | "draft" | "suggest" | "auto";
  allowed: boolean;
  reasonCode: "permission_off" | "permission_allowed";
  writesPerformed: false;
}
export interface AgentOsHookHostSyncReceipt {
  schema: "it-simo.agent-os.hook-host-sync-receipt.v1";
  status: "pass" | "blocked";
  hostId: string;
  installedSynchronized: number;
  missingOnHost: number;
  hooks: AgentOsRegistryRecordView[];
  writesPerformed: false;
  claudeRouteStatus: "deferred_until_2026-08-01";
  rawValuesIncluded: false;
  receiptRef: string;
}
export type AgentOsDraftStatus = "draft" | "validated" | "approved" | "applied" | "rolled_back" | "rejected";
export interface AgentOsDraftView {
  id: string;
  entityType: AgentOsDraftEntityType;
  entityId: string | null;
  baseHash: string;
  draftJson: string;
  diffJson: string;
  status: AgentOsDraftStatus;
  createdBy: string;
  approvedBy: string | null;
  auditId: number | null;
  commitRef: string | null;
  bundleVersion: string | null;
  runtimeSyncStatus: string;
  rollbackRef: string | null;
  createdAt: string;
  updatedAt: string;
  draftHash?: string;
}

export interface AgentTeamGraphView {
  schema: "it-simo.agent-team-graph.v1";
  teamId: string;
  parentRunId: string;
  source: "agent_run_ledger_events";
  nodes: Array<{ id: string; kind: "parent" | "subagent"; status: string; phase: string; roleId: string | null; label?: string }>;
  edges: Array<{ id: string; source: string; target: string; status: string; evidenceBound: boolean; eventCount: number }>;
  events: Array<{ eventId: string; runId: string; parentRunId: string; eventType: string; actor: string; status: string; timestamp: string }>;
  snapshotHash: string;
}

export interface ProviderGoldenComparisonTraceView {
  actor: "codex" | "claude";
  runId: string;
  routeId: string;
  status: "running" | "done" | "failed";
  modelRef: string | null;
  protocolRef: string;
  protocolRevision: number;
  protocolSha256: string;
  brainContextPacketRef: string;
  capabilityEnvelopeRef: string;
  capabilityEnvelopeSha256: string;
  agentOsAttestationHash: string | null;
  resultSha256: string | null;
  ledgerEventCount: number;
  redactionStatus: "secrets-omitted";
}

export interface ProviderGoldenComparisonView {
  schema: "it-simo.provider-golden-comparison.v1";
  taskId: "agent-os-provider-golden-v2";
  parentRunId: string;
  status: "running" | "failed" | "mismatch" | "pass";
  parity: boolean;
  writesPerformed: false;
  traces: ProviderGoldenComparisonTraceView[];
}

export interface ProviderGoldenReadinessView {
  ok: true;
  enabled: boolean;
  ready: boolean;
  providers: { codex: boolean; claude: boolean };
  fixedTaskId: "agent-os-provider-golden-v2";
  arbitraryPromptAccepted: false;
  productWritesAllowed: false;
}

export interface AgentHarnessControlView {
  brainContextPacket: {
    ok: boolean;
    blockId: string;
    mode: string;
    writesPerformed: boolean;
    packet: {
      schema: string;
      currentGoal: string;
      packetRef: string;
      redactionStatus: string;
      connectorReadiness?: string[];
      requiredApprovals?: string[];
      activeRoutines?: string[];
      recentRuns?: string[];
    };
  };
  capabilityEnvelope: {
    ok: boolean;
    blockId: string;
    mode: string;
    writesPerformed: boolean;
    envelope: {
      schema: string;
      runId: string;
      agent: string;
      allowed: string[];
      askRequired: string[];
      blocked: string[];
      readOnlyBoundaries: string[];
      policySource: string;
      redactionStatus?: string;
      envelopeRef: string;
    };
  };
  subagentRegistry: {
    ok: boolean;
    blockId: string;
    mode: string;
    writesPerformed: boolean;
    executionAllowed: boolean;
    mutationAllowed: boolean;
    summary: { total: number; active: number; blocked: number; disabled: number };
    records: Array<{
      id: string;
      name?: string;
      status: string;
      capabilities: string[];
      policyRef: string;
      executionAllowed: boolean;
      mutationAllowed: boolean;
      evidenceRefs: string[];
      redactionStatus: string;
    }>;
  };
  handoff: {
    ok: boolean;
    blockId: string;
    mode: string;
    writesPerformed: boolean;
    executionAllowed: boolean;
    mutationAllowed: boolean;
    packet: {
      handoffRef: string;
      runId: string;
      originatingAgent: string;
      targetAgent: string;
      worktreeState: string;
      ciState: string;
      mergeState: string;
      safeTagState: string;
      evidence: string[];
      openRisks: string[];
      unverifiedClaims: string[];
      redactionStatus: string;
    };
  };
  supervisorLoop: AgentHarnessLoopPreview;
  handoffDrift: AgentHarnessLoopPreview & {
    packetSummary?: {
      provenCurrentFactCount: number;
      assumptionCount: number;
      unverifiedClaimCount: number;
      evidenceCount: number;
      openRiskCount: number;
    };
  };
  forgeCiDrift: AgentHarnessLoopPreview & {
    runtime?: { worktreeState: string; tagsAtHead?: string[] };
    ci?: { status: string; exactHeadMatch: boolean; tokensInPayload: boolean };
  };
  connectorReadiness: AgentHarnessLoopPreview & {
    summary?: { total: number; ready: number; needsOwnerAction: number; missingConfiguration: number };
  };
  routineHealth: AgentHarnessLoopPreview & {
    summary?: { total: number; enabled: number; healthy: number; needsOwnerAction: number; repeatedFailures: number };
  };
  uiUxReality: AgentHarnessLoopPreview & {
    summary?: { totalSurfaces: number; covered: number; needsFollowUp: number; screenshotEvidenceRequired: boolean };
  };
}

export interface AgentHarnessLoopPreview {
  ok: boolean;
  blockId: string;
  schema?: string;
  mode: string;
  writesPerformed: boolean;
  executionAllowed: boolean;
  mutationAllowed: boolean;
  findings?: Array<{ id: string; severity: string; writesPerformed: boolean; proposalOnly: boolean }>;
  nextRecommendations?: string[];
}

export interface ProjectWorkflowStartResult {
  ok: boolean;
  mode: "agentic_project_workflow_runtime";
  policyId: string;
  workflowId: string;
  runId: string;
  conversationId: string;
  routeId: string;
  goal: string;
  executionAllowed: boolean;
  noSilentExecution: boolean;
  evidence: {
    auditId: number;
    evidenceRef: string;
    approvalId: string;
    approvalRef: string;
  };
  routing: {
    orchestratorAgentId: string;
    teamLeadAgentId: string;
    specialistAgentIds: string[];
    requiredCapabilities: string[];
    policyGate: string;
    approvalRequired: boolean;
    blocked: boolean;
  };
  plan: {
    status: "awaiting_approval";
    tasks: Array<{
      id: string;
      label: string;
      status: string;
      executionAllowed: boolean;
      evidenceRequired: boolean;
    }>;
  };
  reviews: Array<{
    id: string;
    routeId: string;
    reviewerAgentIds: string[];
    requiredDecisionBy: string;
    policyGate: string;
    approvalRequired: boolean;
    blocked: boolean;
    executionAllowed: boolean;
  }>;
  approval: {
    id: string;
    status: ApprovalInboxItemView["status"];
    requiredDecisionBy: string;
    mode: ApprovalInboxItemView["mode"];
    riskLevel: ApprovalInboxItemView["riskLevel"];
    summary: string;
    executeOnApproval: boolean;
  };
  ownerDecisionPacket: {
    schema: "it-simo.governed-execution-owner-decision.v1";
    handoffId: string;
    evidenceRef: string;
    approvalRef: string;
    nextGate: "separate_controlled_execution_gate_required";
    ownerPrompt: string;
    requiredOwnerEvidence: string[];
    blockedActions: string[];
    decisionOptions: Array<{
      id: "A" | "B" | "C";
      label: string;
      meaning: string;
      statusOnly: boolean;
      executeOnApproval: boolean;
    }>;
  };
  implementation: {
    status: string;
    executionAllowed: boolean;
    toolExecution: boolean;
    runnerJobs: boolean;
    shellGitDeploy: boolean;
    autoExecute: boolean;
  };
}

export interface WorkerSandboxStatusView {
  policyVersion: "SIMO-WORKER-AGENT-SANDBOX-1";
  readOnlyByDefault: boolean;
  directProdShellAllowed: boolean;
  directHostWriteAllowed: boolean;
  enforcement: {
    allowedPermissionKeys: string[];
    deniedCapabilityReasons: Record<string, string>;
    requiresWorktreeForWrites: boolean;
    requiresApprovalForWrites: boolean;
    requiresCleanApplyToSourceGate: boolean;
  };
  runtimeHardening: {
    workerIdentity: {
      required: boolean;
      registryBacked: boolean;
      rendererCredentialAccess: boolean;
    };
    heartbeat: {
      required: boolean;
      intervalMs: number;
      staleAfterMs: number;
    };
    jobLease: {
      required: boolean;
      ttlMs: number;
      maxRenewals: number;
    };
    timeout: {
      defaultJobTimeoutMs: number;
      hardKillAfterMs: number;
    };
    retry: {
      maxAttempts: number;
      requiresPolicyRecheck: boolean;
      retryUnsafeActions: boolean;
    };
    deadLetter: {
      enabled: boolean;
      reasonCodes: string[];
    };
    execution: {
      enabled: boolean;
      executeEndpoint: string | null;
      policyGated: boolean;
      dryRunOnly: boolean;
    };
  };
  sourceRepo: {
    path: string | null;
    viaEnv: boolean;
  };
  worktree: {
    configured: boolean;
    root: string | null;
  };
  workers: Array<{
    id: "review" | "research" | "code_propose";
    label: string;
    identityRef: string;
    mode: "readonly" | "worktree-write" | "blocked";
    available: boolean;
    capabilities: string[];
    writePath: "none" | "git-worktree";
    requiresApprovalForWrite: boolean;
    prodShellAllowed: boolean;
    directHostWriteAllowed: boolean;
    canPush: boolean;
    canReadSecrets: boolean;
    networkAccess: "blocked";
    reason: string | null;
  }>;
}

export interface RunnerJobBoundaryView {
  boundary: "runner-job-execution-boundary";
  executionEnabled: boolean;
  safeExecutionEnabled: boolean;
  executeEndpoint: string | null;
  safeExecuteEndpoint: string;
  safeExecutePolicyId: string;
  ownerAuthRequired: boolean;
  policyBindingRequired: boolean;
  workerIdentityRequired: boolean;
  workers: Array<{
    id: string;
    label: string;
    available: boolean;
    capabilities: string[];
    allowedJobTypes: string[];
  }>;
  allowedModes: string[];
  deniedActions: string[];
  jobTypes: Array<{
    id: string;
    label: string;
    risk: string;
    execution: "safe" | "denied";
    requiresApproval: boolean;
  }>;
  notes: string[];
}

// SIMO-OMNICHANNEL-ACTIONS-B1: Owner-Device-Sicht für die Geräte-Verwaltung.
export interface OwnerDeviceView {
  id: string;
  kind: string;
  label?: string;
  channel?: string;
  channelRef?: string;
  trusted: boolean;
  enabled: boolean;
}

export type NotificationProvider = "web" | "mobile_push" | "teams";
export type NotificationReplyChannel = "web" | "mobile" | "teams";
export type NotificationReplyAction =
  | "approve"
  | "reject"
  | "stop"
  | "continue"
  | "retry"
  | "show_evidence"
  | "open_details"
  | "custom";

export interface NotificationDeliveryView {
  id: string;
  notificationId: string;
  provider: NotificationProvider;
  channelRef: string | null;
  status: "pending" | "queued" | "delivered" | "failed" | "blocked";
  externalId: string | null;
  error: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationView {
  id: string;
  type:
    | "approval.requested"
    | "job.completed"
    | "job.stopped"
    | "job.failed"
    | "job.needs_input"
    | "learning.proposal.created"
    | "security.warning"
    | "deploy.blocked";
  title: string;
  body: string;
  urgency: "low" | "normal" | "high";
  correlationId: string;
  sourceKind: "approval" | "learning_proposal" | "job" | "security" | "deploy" | "system" | null;
  sourceId: string | null;
  userId: string | null;
  status: "unread" | "read" | "archived";
  createdAt: string;
  expiresAt: string | null;
  deliveries: NotificationDeliveryView[];
}
export type NotificationAlertLane = "owner_escalation" | "priority" | "routine" | "muted";
export type NotificationCenterAction = "approve" | "reject" | "acknowledge" | "snooze" | "open_details" | "show_evidence" | "retry" | "continue";
export interface NotificationCenterItemView {
  id: string;
  title: string;
  body: string;
  status: "unread" | "read" | "archived";
  urgency: "low" | "normal" | "high";
  category: "decision" | "work" | "risk" | "ops";
  priority: number;
  sourceKind: NotificationView["sourceKind"];
  sourceId: string | null;
  correlationId: string;
  createdAt: string;
  expiresAt: string | null;
  channels: string[];
  availableActions: NotificationCenterAction[];
  alertLane: NotificationAlertLane;
  ownerOnlyEscalation: boolean;
  spamWeight: number;
}

export interface HealthInfo {
  ok: boolean;
  version: string;
  time: string;
  runtime?: {
    commit: string | null;
    branch: string | null;
    sourcePath: string;
    cwd: string;
    node: string;
    environment: string;
  };
  ollama: { ok: boolean; baseUrl: string; models: string[] };
}

export interface CodexCodeWorkflowSessionView {
  runId: string;
  conversationId: string | null;
  goal: string;
  runStatus: "running" | "awaiting_approval" | "done" | "error" | "cancelled";
  runPhase: string;
  branch: string;
  baseRef: string;
  files: string[];
  testCommands: string[];
  approvalId: string;
  approvalStatus: "pending" | "approved" | "rejected" | "expired" | "cancelled";
  evidenceRef: string;
  auditId: number | null;
  createdAt: string;
  updatedAt: string;
  stages: Array<{ id: string; title: string; status: string; executionAllowed: false; evidenceRequired: boolean }>;
  gates: Record<string, string>;
  boundary: {
    directExecuteEndpoint: null;
    shellAllowed: false;
    gitPushAllowed: false;
    prCreateAllowed: false;
    mergeAllowed: false;
    deployAllowed: false;
  };
}

export interface ControlledShellPlanView {
  ok: true;
  mode: "controlled_shell_plan";
  policyId: string;
  toolId: "controlled.shell.execute";
  requestId: string;
  runId: string;
  command: string;
  reason: string;
  expectedOutput: string;
  requiresApproval: true;
  executeOnApproval: false;
  executionAllowedNow: false;
  approval: { id: string; status: string; requiredDecisionBy: string; executeOnApproval: false };
  evidence: { id: number; auditId: number; requestId: string; approvalId: string };
  boundary: {
    shellAllowedWithoutApproval: false;
    gitAllowed: false;
    deployAllowed: false;
    vpsAllowed: false;
    allowedCommand: true;
  };
}

export interface BuildInfo {
  version: string;
  generatedAt: string;
  runtime: {
    commit: string | null;
    branch: string | null;
    sourcePath: string;
    cwd: string;
    node: string;
    environment: string;
  };
}

export interface ConnectorStatusView {
  id: string;
  name: string;
  configured: boolean;
  reachable: boolean;
  message?: string;
  issueCode?: "credentials_missing" | "authorization_missing" | "reauth_required" | "api_disabled" | "service_error";
  actionLabel?: string;
  state?: "connected" | "needs_reauth" | "missing_credentials" | "not_authorized" | "degraded" | "not_configured";
  action?: "none" | "connect" | "reauth" | "configure" | "check_api";
  risk?: string;
  lastSuccessAt?: string | null;
}

export interface BriefingItemView {
  connector: string;
  kind: "calendar" | "mail" | "pr" | "issue" | "ticket" | "chat" | "notification";
  title: string;
  subtitle?: string;
  url?: string;
  timestamp?: string;
  priority?: "low" | "medium" | "high";
}

export type AssistantMode =
  | "personal_chat"
  | "briefing"
  | "calendar"
  | "mail"
  | "project_status"
  | "code_review"
  | "vps_status"
  | "vision"
  | "smart_home"
  | "blocked_action";

export type SanityStatus =
  | "passed"
  | "failed_language"
  | "failed_topic"
  | "failed_persona"
  | "failed_tool_claim"
  | "regenerated"
  | "fallback_used";

export interface AssistantDebugView {
  assistantMode: AssistantMode;
  userLanguage: "de" | "en";
  model: string;
  activeToolGroups: string[];
  blockedToolGroups: string[];
  activeToolNames: string[];
  usedToolNames: string[];
  latencies: {
    totalMs: number;
    llmMs: number;
    toolMs: number;
  };
  sanityStatus: SanityStatus;
  sanityInitialFailure?: Exclude<SanityStatus, "passed" | "regenerated" | "fallback_used"> | null;
  degradedReason?: "timeout" | "cancelled" | "service_unavailable" | "generic" | "no_progress" | null;
  harness?: {
    routeId: "simo.native.agentgraph";
    bound: true;
    protocolRef: string;
    brainContextPacketRef: string;
    capabilityEnvelopeRef: string;
    protocolSha256: string;
    capabilityEnvelopeSha256: string;
    revision: number;
    previousProtocolRef: string | null;
  } | null;
}

export interface CockpitBriefing {
  connectors: ConnectorStatusView[];
  counts: {
    total: number;
    prs: number;
    notifications: number;
    tickets: number;
    mails: number;
    calendar: number;
  };
  grouped: Record<BriefingItemView["kind"], BriefingItemView[]>;
  durationMs: number;
}

// ── CP-08 — Control-Plane-Overview-Typen (Server: routes/control-plane-summary.ts,
// routes/human-gates.ts, routes/mission-plan-approval.ts) ─────────────────────

/** GET /api/control-plane/summary — System-, Queue-, Gate- und Scope-Übersicht. */
export interface ControlPlaneSummary {
  ok: boolean;
  schema: string;
  generatedAt: string;
  system: {
    dbOk: boolean;
    supervisor: {
      tickIntervalMsDefault: number;
      enabledByDefault: boolean;
      executor: { configured: boolean; running: boolean } | null;
    };
  };
  /** OPNAV1 Slice 3 — kanonische Run-/Mission-/Lease-Semantik (Chat-/Tool-Runs ausgeschlossen). */
  semantics?: {
    contract: string;
    missions: {
      active: number;
      byStatus: Array<{ status: string; count: number }>;
      activeList: Array<Record<string, unknown>>;
    };
    runs: {
      nonTerminal: number;
      byStatus: Array<{ status: string; count: number }>;
      activeList: Array<Record<string, unknown>>;
    };
    toolRuns: { countedAsRuns: boolean; note: string };
    chatRuns: { countedAsRuns: boolean; note: string };
    leases: {
      active: number;
      list: Array<Record<string, unknown>>;
    };
  };
  queue: {
    states: Array<{ state: string; count: number }>;
    scopeUnverified: number;
  };
  /** S0.4c: Primae-Entscheidungen (Header-Wahrheit); null = WARMING. */
  gates: { open: number | null };
  findings: { open: number };
  resources: {
    classes: Array<{ classId: string; capacity: number; active: number }>;
  };
  scopes: { verified: number; failClosed: number };
}

export interface ControlPlaneSupervisorTickView {
  tickId: string;
  finishedAt: string;
  durationMs: number;
  llmCalls: 0;
  transitions: number;
  admitted: number;
  waitingResource: number;
  gatesBundled: number;
  skipped: number;
}

export interface ControlPlaneSupervisorEventView {
  eventId: string;
  occurredAt: string;
  eventType: string;
  runId: string;
  missionId: string | null;
  workitemId: string | null;
  title: string | null;
}

export interface ControlPlaneSupervisorView {
  ok: boolean;
  schema: string;
  generatedAt: string;
  health: {
    dbOk: boolean;
    schedulerConfigured: boolean;
    schedulerRunning: boolean;
    executorConfigured: boolean;
    executorRunning: boolean;
    contract: string;
    hostLabel: string;
  };
  cadence: { intervalMs: number; llmCallsPerTick: number; letzteDauerMs: number | null };
  lastTick: ControlPlaneSupervisorTickView | null;
  tickHistory: ControlPlaneSupervisorTickView[];
  lastAdmission: ControlPlaneSupervisorEventView | null;
  nextEligible: Array<{
    workitemId: string;
    title: string;
    priority: string;
    missionId: string | null;
    queueState: string | null;
    warumWartend: string;
    naechsteBedingung: string;
    wartetAuf: string[];
  }>;
  recentTransitions: ControlPlaneSupervisorEventView[];
  recentErrors: Array<{ kind: string; at: string; detail: string }>;
}

export type HumanActorClassView =
  | "OWNER_DECISION"
  | "AUTHORIZED_OPERATOR"
  | "ACCOUNT_HOLDER"
  | "PHYSICAL_OPERATOR";

export interface HumanGateView {
  gateId: string;
  missionId: string;
  workitemId: string | null;
  actorClass: HumanActorClassView;
  actionClass: string;
  targetRef: string;
  whyRequired: string;
  exactAction: string;
  risk: string;
  whatContinuesAfter: string;
  status: "OPEN" | "RESOLVED" | "WITHDRAWN" | "EXPIRED";
  createdAt: string;
  expiresAt: string;
}

export interface GateBundleGateView {
  gateId: string;
  actorClass: string;
  actionClass: string;
  whyRequired: string;
  exactAction: string;
  risk: string;
  whatContinuesAfter: string;
  alternativesTried: string | null;
  targetRef: string;
  status: string;
  expiresAt: string;
}

export interface GateBundleView {
  bundleId: string;
  missionId: string;
  nonce: string;
  freshUntil: string;
  createdAt: string;
  gates: GateBundleGateView[];
}

export interface GateBundlesResponse {
  ok: boolean;
  bundles: GateBundleView[];
}
// ── S0 — Owner-Gate-Materialisierung (Server: agent-exchange-service.ts,
// Abschnitt "S0 — Owner-Gate-Materialisierung"; Routen in routes/agent-exchange.ts) ──
export interface OwnerGateView {
  gateId: string;
  gateType: "AUTHORITY_ADVANCE";
  action: "AUTHORIZE_SHADOW";
  /** EINGEFRORENER Bundle-Snapshot (S0.1): exakt diese Missionen, keine Late-Joiner. */
  missionIds: string[];
  batch: boolean;
  /** S0.1: Missions-Kopfzeilen fuer die aufklappbare "Affected missions"-Sektion. */
  missions: { id: string; headline: string }[];
  /** S0.1: Familien-Schluessel (Programm-Korrelation). */
  familyKey: string;
  currentAuthorityState: string;
  nextAuthorityState: string;
  ownerActionRequired: true;
  ownerExplanation: string;
  createdAt: string;
  expiresAt: string | null;
  status: "OPEN" | "HELD";
  held: { reason: string; heldBy: string; heldAt: string } | null;
}

/** Ehrlichkeitszustand des Gate-Lesepfads: nie 0 behaupten bei Unwissen. */
export type OwnerGateReadStateUi = "LIVE" | "WARMING" | "SOURCE_UNAVAILABLE" | "ERROR";

/**
 * S0.3 — Abort-Semantik: ein ABORT durch Supersede/Unmount/externen Abbruch
 * ist KEIN Anzeigefehler. Chrome meldet solche fetch-Abbrueche als
 * DOMException(name='AbortError', 'The user aborted a request.') — genau dieser
 * rohe String landete zuvor als "Inbox-Fehler" in der Owner-UI.
 */
export function isAbortError(err: unknown): boolean {
  return (
    typeof err === "object" &&
    err !== null &&
    (err as { name?: unknown }).name === "AbortError"
  );
}

export interface OwnerGateStatusResponse {
  ok: boolean;
  state: OwnerGateReadStateUi;
  /** S0.1: EIN Bundle je Familie/Einzelmission statt EINEM Global-Batch. */
  bundles: OwnerGateView[] | null;
  openCount: number | null;
  message?: string | null;
}

/** Ehrliches Per-Mission-Ergebnis des Batch-Advance (auch bei Teil-Fehler). */
export interface BatchAdvanceMissionResultView {
  missionId: string;
  ok: boolean;
  previous: string | null;
  new: string | null;
  actor: string;
  authorityClass: string | null;
  receiptId: string | null;
  timestamp: string | null;
  reason: string | null;
  code: string | null;
  message: string | null;
  /** S0.1-fix: aktueller Zustand bei 'already_advanced'/'gate_not_open'. */
  currentState?: string | null;
}

export interface BatchAdvanceResponse {
  ok: boolean;
  results?: BatchAdvanceMissionResultView[];
  gateId?: string | null;
  gateStatus?: string;
  reason?: string;
  message?: string;
}

/** GET /api/human-gates — nach actor_class gruppierte echte Owner-Arbeit. */
export interface HumanGatesResponse {
  ok: boolean;
  status: string;
  total: number;
  byActorClass: Record<HumanActorClassView, HumanGateView[]>;
}

export interface MissionListItemView {
  id: string;
  goal: string;
  status: string;
  scope?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface MissionsListResponse {
  ok: boolean;
  missions: MissionListItemView[];
}

export interface MissionItemView {
  id: string;
  title: string;
  priority: string;
  status: string;
  queue_state: string | null;
  queue_state_reason: string | null;
  writer_scope: string | null;
  scope_verified: number | null;
  /** OWNEXP Slice 6 — Ausführungsdetails je Teilaufgabe (Modell-/Ressourcen-Policy, Writer-Lease). */
  model_policy_json?: string | null;
  resource_class_json?: string | null;
  lease_holder?: string | null;
  lease_acquired_at?: string | null;
  lease_expires_at?: string | null;
  /** OWNEXP Red-Team H2: kanonischer Wartegrund (erklaereWartegrund) — der
   *  Dialog zitiert ihn statt selbst abzuleiten (SINGLE_TRUTH). */
  warum_wartend?: string | null;
  naechste_bedingung?: string | null;
}

export type AgentRoomFeedKindView = "message" | "run_event" | "evidence_ref" | "open_gate" | "finding";

export interface AgentRoomFeedItemView {
  id: string;
  kind: AgentRoomFeedKindView;
  occurredAt: string;
  role?: string;
  type?: string;
  severity?: string;
  [feld: string]: unknown;
}

export interface AgentRoomFeedResponse {
  ok: boolean;
  missionId: string;
  items: AgentRoomFeedItemView[];
  vocabulary: string;
}

export interface MissionCheckpointView {
  checkpoint_id: string;
  mission_id: string;
  seq: number;
  current_block: string;
  mission_state: string;
  created_at: string;
  [feld: string]: unknown;
}

export interface MissionCheckpointsResponse {
  ok: boolean;
  checkpoints: MissionCheckpointView[];
  count: number;
  limit: number;
}

export interface MissionEvidenceItemView {
  evidenceId: number | string;
  subjectType: string;
  subjectId: string;
  evidenceKind: string;
  provenance: string | null;
  producer: string | null;
  artifactRef: string | null;
  sha256: string | null;
  /** CP-16: Verifikationsstand des Beleg-Inhalts (VERIFIED_CONTENT | REFERENCE_ONLY | UNKNOWN). */
  contentBinding: string | null;
  mediaType: string | null;
  sizeBytes: number | null;
  createdAt: string;
}

export interface MissionEvidenceResponse {
  missionId: string;
  evidence: MissionEvidenceItemView[];
}
export interface QueueItemView {
  id: string;
  title: string;
  goal: string | null;
  priority: string;
  status: string;
  missionId: string | null;
  missionGoal: string | null;
  queueState: string | null;
  queueStateReason: string | null;
  managed: boolean;
  writerScope: string | null;
  scopeVerified: boolean;
  modelPolicy: string | null;
  resourceClass?: string | null;
  leaseHolder: string | null;
  leaseExpiresAt: string | null;
  createdAt: string;
  updatedAt: string;
  /** OPNAV1 Slice 6 — letztes Queue-Event (Typ + Zeitpunkt) aus control_plane_run_events. */
  lastEvent?: { eventType: string; occurredAt: string } | null;
  /** OWNEXP (Codex UX-01 / SINGLE_TRUTH): kanonischer Wartegrund + nächste
   *  Bedingung — serverseitig aus derselben Ableitung wie der Supervisor
   *  (erklaereWartegrund). Flächen zitieren diese Felder; sie leiten den
   *  Wartegrund nie selbst ab. */
  warumWartend?: string | null;
  naechsteBedingung?: string | null;
  unmetDependencies: Array<{ dependsOn: string; kind: string }>;
  blocksCount: number;
}

export interface QueueResponse {
  ok: boolean;
  items: QueueItemView[];
  boundedTo: number;
  /** OWNEXP Live-Deckel: vom Server wegen Byte-Budget (Proxy-Kappe) gekürzte Items. */
  truncated?: number;
  offset?: number;
}
export interface MissionDetailResponse {
  ok: boolean;
  mission: MissionListItemView & Record<string, unknown>;
  items: MissionItemView[];
  checkpointCount: number;
}


export interface AgentOsAutonomyModeView {
  id: "research" | "plan" | "implement" | "review" | "autonomous_gated" | "ask_more";
  label: string;
  clarification: string;
  webResearch: string;
  toolPolicy: string;
  permissionFloor: string;
  approvalThreshold: string;
}
export interface AgentOsSettingsView {
  schema: string;
  layout: "mission_console";
  primaryRuntime: "ollama:gex44";
  activeMode: AgentOsAutonomyModeView["id"];
  clarificationPolicy: "minimal" | "balanced" | "high_precision";
  webResearch: { enabled: boolean; askBeforeNetwork: boolean };
  reads: { promptBeforeRead: boolean; allowWebResearch: boolean; gatherRepoContext: boolean };
  changes: { requireApproval: boolean; diffPreviewRequired: boolean; worktreeRequired: boolean };
  modelDefaults: { planning: string; reasoning: string; code: string; review: string };
  autonomyModes: AgentOsAutonomyModeView[];
}
export interface AgentOsRuleView {
  id: string;
  title: string;
  body: string;
  enabled: boolean;
  category: string;
}
export interface AgentOsExecutionPolicyView {
  readsPrompt: boolean;
  changesRequireApproval: boolean;
  shellGitDeployRequireApproval: boolean;
  connectorWritesRequireApproval: boolean;
  memoryMutationRequireApproval: boolean;
  ruleChangesRequireApproval: boolean;
  approvalRequired: boolean;
  expectedAction: AgentOsRunCreate["expectedAction"];
  startPolicy: AgentOsStartPolicy;
  primaryRuntime: "ollama:gex44";
}
export type AgentOsStartPolicy = "plan_only" | "run_read" | "run_gated";
export interface AgentOsRunCreate {
  prompt: string;
  mode?: AgentOsAutonomyModeView["id"];
  startPolicy?: AgentOsStartPolicy;
  repoPath?: string;
  expectedAction?: "read" | "write" | "shell" | "git" | "deploy" | "connector_write" | "memory_write" | "rule_change";
  branch?: string;
  baseRef?: string;
  files?: string[];
  testCommands?: string[];
}
export interface AgentOsRunView {
  id: string;
  conversationId: string;
  model: string | null;
  phase: string;
  status: "running" | "awaiting_approval" | "done" | "error" | "cancelled";
  roleId: string | null;
  assistantMode: string | null;
}
export interface AgentOsReplayLifecycleView {
  prompt: boolean;
  plan: boolean;
  worktree: boolean;
  diff: boolean;
  checks: boolean;
  approval: boolean;
  pr: boolean;
  merge: boolean;
  safeTag: boolean;
  evidence: boolean;
}
export interface AgentOsReplayTimelineItemView {
  kind: string;
  at: string;
  title: string;
  status: string;
  ref?: string;
  auditId?: number | null;
  type?: string;
}
export interface AgentOsReplayCompletenessView {
  complete: boolean;
  missing: string[];
}
export interface AgentOsHistoryRunView extends AgentOsRunView {
  createdAt: string;
  updatedAt: string;
  prompt: { summary: string; text?: string | null; rawAvailable: boolean; redaction?: string | null };
  plan: Record<string, unknown> | null;
  worktree: Record<string, unknown> | null;
  diff: Record<string, unknown> | null;
  approvals: Array<{ id: string; status: string; summary: string; toolId: string; decidedAt?: string | null; auditId?: number | null }>;
  checks: Record<string, unknown> | null;
  pr: Record<string, unknown> | null;
  merge: Record<string, unknown> | null;
  safeTag: Record<string, unknown> | null;
  evidence: Record<string, unknown> | null;
  lifecycle: AgentOsReplayLifecycleView;
  completeness: AgentOsReplayCompletenessView;
  evidenceCount: number;
  timelineCount: number;
}
export interface AgentOsReplayView {
  run: AgentOsRunView & { createdAt: string; updatedAt: string; runtime?: string; auditId?: number | null };
  prompt: { summary: string; text?: string | null; rawAvailable: boolean; redaction?: string | null };
  request: { mode?: string | null; expectedAction?: string | null; primaryRuntime?: string | null; repoPath?: unknown };
  plan: Record<string, unknown> | null;
  worktree: Record<string, unknown> | null;
  diff: Record<string, unknown> | null;
  approvals: Array<{ id: string; status: string; summary: string; toolId: string; decidedAt?: string | null; auditId?: number | null }>;
  checks: Record<string, unknown> | null;
  pr: Record<string, unknown> | null;
  merge: Record<string, unknown> | null;
  safeTag: Record<string, unknown> | null;
  evidence: Record<string, unknown> | null;
  evidenceLedger: Array<{ id: number; actionId: string; resultStatus: string; summary?: string | null; auditId?: number | null; createdAt: string }>;
  lifecycle: AgentOsReplayLifecycleView;
  completeness: AgentOsReplayCompletenessView;
  timeline: AgentOsReplayTimelineItemView[];
}
export interface AgentOsReplayCheckpointCreate {
  type: "plan" | "workspace" | "worktree" | "diff" | "checks" | "approval" | "pr" | "merge" | "safe_tag" | "evidence";
  status: string;
  title: string;
  summary: string;
  data?: Record<string, unknown>;
  evidenceRef?: string;
  occurredAt?: string;
}
export interface AgentOsRunCreateResponse {
  ok: boolean;
  run: AgentOsRunView;
  executionPolicy: AgentOsExecutionPolicyView;
  eventsSummary: Array<{ type: string; summary: string; at: string }>;
  replayCompleteness: { complete: boolean; required: string[]; missing: string[] };
  approvalRequired: boolean;
  approval?: ApprovalView | null;
}
export interface AgentOsRunEventView {
  type: string;
  summary: string;
  at: string;
}
export interface AgentOsMonitoringView {
  schema: "simot-agent-os-run-monitoring.v1";
  mode: "read_only_monitoring";
  requestId: string;
  generatedAt: string;
  window: { limit: number; staleAfterMinutes: number };
  counts: {
    total: number;
    running: number;
    awaitingApproval: number;
    done: number;
    error: number;
    cancelled: number;
    pendingApprovals: number;
    staleRuns: number;
    missingEvidence: number;
    evidenceCount: number;
  };
  alerts: Array<{
    id: string;
    type: "stale_run" | "approval_waiting" | "run_error" | "missing_evidence";
    severity: "info" | "warning" | "critical";
    runId: string;
    title: string;
    summary: string;
    requestId: string;
    evidenceRef: string;
    createdAt: string;
    safeActions: Array<"open_details" | "show_evidence">;
    blockedActions: Array<"execute" | "retry" | "shell" | "git" | "deploy" | "connector_write">;
  }>;
  gates: {
    executionAllowed: false;
    sendsNotifications: false;
    createsRunnerJobs: false;
    mutatesRuns: false;
    requiresOwnerSession: true;
    safeActionsOnly: true;
  };
  recentRuns: Array<{
    id: string;
    status: AgentOsRunView["status"];
    phase: string;
    model: string | null;
    updatedAt: string;
    prompt: { summary: string; text?: string | null; rawAvailable: boolean; redaction?: string | null };
    completeness: AgentOsReplayCompletenessView;
    evidenceCount: number;
    pendingApprovals: number;
  }>;
}
export interface OwnerSessionView {
  id: string;
  current: boolean;
  channel: "web" | "mobile" | "tablet" | "api" | "maintenance" | "unknown";
  clientLabel: string | null;
  createdAt: string;
  lastSeenAt: string;
  expiresAt: string;
}

export interface OwnerSessionPolicyView {
  absoluteTtlMs: number;
  idleTimeoutMs: number;
  maxActiveSessions: number;
}

export interface OwnerSessionsResponse {
  ok: boolean;
  currentSessionId: string;
  policy: OwnerSessionPolicyView;
  sessions: OwnerSessionView[];
}

// ─── SIMO-MISSION-PLAN-REVIEW-AND-APPROVAL-1 (B01 Slice 3) ────────────────
// Owner sieht VOR jeder Ausfuehrung, was SIMO tun moechte, und entscheidet.
// Wire-Shape spiegelt `apps/server/src/mission-plan-approval.ts` 1:1.

export interface MissionSummaryView {
  id: string;
  ownerId: string;
  goal: string;
  scope: string;
  outOfScope: string;
  status: string;
  missionVersion: number;
  /** SIMO-MISSION-OWNER-ACCESS-1 (B01 Slice 6, Luecke 2): aktuell gueltige Plan-Generation. */
  planVersion: number;
  createdAt: string;
  updatedAt: string;
}

export interface MissionPlanStepView {
  id: string;
  order: number;
  title: string;
  goal: string;
  rationale: string | null;
  status: string;
  /** Ehrliche Wiederverwendung von `product_scope` — "betroffene Projekte/Repositories". */
  productScope: string;
  requiredCapabilities: string[];
  approvalsNeeded: string[];
}

/**
 * SIMO-MISSION-OWNER-ACCESS-1 (B01 Slice 6, Luecke 2) — ein Eintrag der
 * Ueberarbeitungs-Historie. Wire-Shape spiegelt
 * `MissionPlanRevisionHistoryEntryView` 1:1.
 */
export interface MissionPlanRevisionHistoryEntryView {
  planVersionBefore: number;
  planVersionAfter: number;
  note: string;
  createdAt: string;
}

export interface MissionPlanReviewView {
  mission: {
    id: string;
    goal: string;
    scope: string;
    outOfScope: string;
    status: string;
    missionVersion: number;
    /** SIMO-MISSION-OWNER-ACCESS-1 (B01 Slice 6, Luecke 2): aktuell gueltige Plan-Generation. */
    planVersion: number;
    createdAt: string;
    updatedAt: string;
  };
  planExists: boolean;
  steps: MissionPlanStepView[];
  /** SIMO-MISSION-OWNER-ACCESS-1 (B01 Slice 6, Luecke 2): chronologische Ueberarbeitungs-Historie, leer wenn nie ueberarbeitet. */
  revisionHistory: MissionPlanRevisionHistoryEntryView[];
  decision: {
    alreadyDecided: boolean;
    decidedStatus: string | null;
  };
  /**
   * "Wenn die Datenlage etwas nicht hergibt, steht das da." Diese drei
   * Kategorien erfasst das Datenmodell heute nicht strukturiert — der Server
   * liefert dafuer ehrliche, wertfreie Klartexte statt erfundener Felder.
   */
  notCapturedByModel: {
    risk: string;
    expectedEvidence: string;
    openAssumptions: string;
  };
}

export type MissionPlanDecisionKind = "approve" | "reject" | "request_revision" | "stop";

export type MissionPlanDecisionOutcome =
  | { ok: true; mission: MissionSummaryView }
  | { ok: false; status: number; error: string; message?: string; mission?: MissionSummaryView };

// ─── SIMO-MISSION-PROGRESS-PROJECTION-1 (B01 Slice 5) ──────────────────────
// Missionsfortschritt wird ausschliesslich abgeleitet — Wire-Shape spiegelt
// `apps/server/src/mission-progress-projection.ts` 1:1. Rein lesend, kein
// Schreibpfad in diesem Slice.

export type MissionStepProgressStatus =
  | "stopped"
  | "done"
  | "bind_operation_outcome_unknown"
  | "running"
  | "awaiting_plan_approval"
  | "waiting_for_owner"
  | "recovery_required"
  | "failed"
  | "retrying"
  | "queued"
  | "blocked_unspecified"
  | "paused"
  | "archived";

export interface MissionStepRunView {
  runId: string;
  status: string;
  phase: string;
  finalAnswer: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface MissionStepProgressView {
  id: string;
  order: number;
  title: string;
  goal: string;
  rationale: string | null;
  rawStatus: string;
  rawBlockedReason: string | null;
  progressStatus: MissionStepProgressStatus;
  blockingStepId: string | null;
  bound: boolean;
  boundAt: string | null;
  bindOperationState: "pending" | "executing" | "completed" | "failed" | "outcome_unknown" | null;
  run: MissionStepRunView | null;
  evidence: unknown[];
}

export interface MissionProgressCounts {
  total: number;
  done: number;
  failed: number;
  active: number;
  waitingForOwner: number;
  recoveryRequired: number;
  stopped: number;
  notStarted: number;
}

export type MissionOverallProgressStatus =
  | "draft"
  | "waiting_for_plan_approval"
  | "plan_rejected"
  | "revision_requested"
  | "approved"
  | "running"
  | "waiting_for_owner"
  | "recovery_required"
  | "partially_completed"
  | "completed"
  | "failed"
  | "stopped"
  | "unknown";

export interface MissionNextSafeStepView {
  kind: "author_plan" | "owner_plan_decision" | "owner_recovery" | "owner_approval" | "bind_step" | "none";
  stepId: string | null;
  label: string;
  reason: string;
}

export interface MissionProviderLaneRunSummaryView {
  runId: string;
  ownerId: string;
  branch: string;
  providerMode: string;
  state: string;
  phase: string | null;
  pullRequestNumber: number | null;
  pullRequestUrl: string | null;
  pullRequestState: string | null;
  startedAt: string;
  updatedAt: string;
  finishedAt: string | null;
}

export interface MissionProgressView {
  mission: {
    id: string;
    goal: string;
    scope: string;
    outOfScope: string;
    statusRaw: string;
    missionVersion: number;
    createdAt: string;
    updatedAt: string;
  };
  overallStatus: MissionOverallProgressStatus;
  counts: MissionProgressCounts;
  nextSafeStep: MissionNextSafeStepView | null;
  steps: MissionStepProgressView[];
  relatedProviderLaneRuns: MissionProviderLaneRunSummaryView[];
  notCapturedByModel: {
    commitsPushesPullRequests: string;
  };
}

export const api = {
  // SIMO-OWNER-SESSION-MANAGEMENT-UI-1 — die Sicherheitsflaeche fuer Sessions.
  // Der Server liefert NIE Token oder Hashes; die Typen bilden das ab.
  ownerSessions: () => jfetch<OwnerSessionsResponse>("/api/auth/sessions"),
  revokeOwnerSession: (sessionId: string) =>
    jfetch<{ ok: boolean }>(`/api/auth/sessions/${encodeURIComponent(sessionId)}/revoke`, { method: "POST" }),
  revokeOtherOwnerSessions: () =>
    jfetch<{ ok: boolean; revoked: number; keptSessionId: string }>("/api/auth/sessions/revoke-others", { method: "POST" }),
  //_SECRET-LANE (POST-GATE-INCIDENT): offene Credential-Requests (nur
  //_Metadaten — der Server liefert nie Werte, die Antwort nie ein Echo).
  secretRequests: () =>
    jfetch<{ items: Array<{ id: string; workitemId: string; missionId: string; secretKey: string; secretLabel: string; status: string; expiresAt: string }> }>("/api/secret-requests"),
  secretProvision: (requestId: string, value: string) =>
    jfetch<{ ok: boolean; key: string; status: string; fingerprint: string; hinweis: string }>(
      `/api/secret-requests/${encodeURIComponent(requestId)}/provision`,
      { method: "POST", body: JSON.stringify({ value }) },
    ),
  agentOsSettings: () => jfetch<{ ok: boolean; settings: AgentOsSettingsView }>("/api/agent-os/settings"),
  updateAgentOsSettings: (body: Partial<Pick<AgentOsSettingsView, "activeMode" | "clarificationPolicy" | "webResearch" | "reads" | "changes" | "modelDefaults">>) =>
    jfetch<{ ok: boolean; settings: AgentOsSettingsView }>("/api/agent-os/settings", { method: "PUT", body: JSON.stringify(body) }),
  agentOsRules: () => jfetch<{ ok: boolean; rules: AgentOsRuleView[] }>("/api/agent-os/rules"),
  updateAgentOsRules: (rules: AgentOsRuleView[]) =>
    jfetch<{ ok: boolean; rules: AgentOsRuleView[] }>("/api/agent-os/rules", { method: "PUT", body: JSON.stringify({ rules }) }),
  createAgentOsRun: (body: AgentOsRunCreate) =>
    jfetch<AgentOsRunCreateResponse>("/api/agent-os/runs", { method: "POST", body: JSON.stringify(body) }),
  agentOsHistory: (query?: { q?: string; status?: AgentOsRunView["status"]; limit?: number; offset?: number }) => {
    const params = new URLSearchParams();
    if (query?.q) params.set("q", query.q);
    if (query?.status) params.set("status", query.status);
    if (query?.limit) params.set("limit", String(query.limit));
    if (query?.offset) params.set("offset", String(query.offset));
    const suffix = params.toString();
    return jfetch<{ ok: boolean; runs: AgentOsHistoryRunView[]; pagination?: { limit: number; offset: number; total: number; hasMore: boolean } }>(`/api/agent-os/history${suffix ? `?${suffix}` : ""}`);
  },
  agentOsReplay: (runId: string) => jfetch<{ ok: boolean; replay: AgentOsReplayView }>(`/api/agent-os/runs/${encodeURIComponent(runId)}/replay`),
  agentOsMonitoring: () => jfetch<{ ok: boolean; monitoring: AgentOsMonitoringView }>("/api/agent-os/monitoring"),
  createAgentOsReplayCheckpoint: (runId: string, body: AgentOsReplayCheckpointCreate) =>
    jfetch<{ ok: boolean; checkpoint: AgentOsReplayCheckpointCreate & { evidenceId: number; auditId: number } }>(
      `/api/agent-os/runs/${encodeURIComponent(runId)}/replay-checkpoints`,
      { method: "POST", body: JSON.stringify(body) },
    ),
  agentOsRunEvents: async function* (
    runId: string,
    signal?: AbortSignal,
  ): AsyncIterable<AgentOsRunEventView> {
    const path = `/api/agent-os/runs/${encodeURIComponent(runId)}/events`;
    const headers: Record<string, string> = { Accept: "text/event-stream" };
    const res = await fetch(path, {
      headers,
      signal,
      credentials: "same-origin",
    });
    if (!res.ok || !res.body) {
      const text = await res.text().catch(() => "");
      throw buildOwnerApiError(path, res, text);
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let idx: number;
      while ((idx = buffer.indexOf("\n\n")) !== -1) {
        const raw = buffer.slice(0, idx).trim();
        buffer = buffer.slice(idx + 2);
        if (!raw) continue;
        const lines = raw.split("\n");
        let dataStr = "";
        for (const line of lines) {
          if (line.startsWith("data:")) dataStr += line.slice(5).trim();
        }
        try {
          yield JSON.parse(dataStr) as AgentOsRunEventView;
        } catch {
          // ignore malformed frames
        }
      }
    }
  },
  /**
   * SIMO-OWNER-CONSOLE-LIVE-UPDATES-1 — Server-Sent-Events-Stream fuer die
   * Owner-Konsole, PARALLEL zum bestehenden 5-Sekunden-Polling
   * (`providerLaneRuns`). Gleiches Muster wie `agentOsRunEvents`: `fetch` +
   * `body.getReader()` statt `EventSource`, weil nur so der Header
   * `x-simo-session-token` gesetzt werden kann (siehe
   * `apps/server/src/routes/provider-execution-lane.ts`, `requireOwner`).
   */
  providerLaneStream: async function* (
    signal?: AbortSignal,
    lastEventId?: number,
  ): AsyncIterable<{ type: "snapshot" | "run"; seq: number; runs?: unknown[]; run?: unknown }> {
    const path = "/api/provider-lane/stream";
    const headers: Record<string, string> = { Accept: "text/event-stream" };
    if (typeof lastEventId === "number" && Number.isFinite(lastEventId) && lastEventId > 0) {
      headers["Last-Event-ID"] = String(lastEventId);
    }
    const res = await fetch(path, {
      headers,
      signal,
      credentials: "same-origin",
    });
    if (!res.ok || !res.body) {
      const text = await res.text().catch(() => "");
      throw buildOwnerApiError(path, res, text);
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let idx: number;
      while ((idx = buffer.indexOf("\n\n")) !== -1) {
        const raw = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        if (!raw.trim()) continue;
        const lines = raw.split("\n");
        let eventType = "";
        let dataStr = "";
        for (const line of lines) {
          // Kommentarzeilen (Heartbeat/Connect-Marker) beginnen mit ":" und
          // tragen keine Nutzdaten — sie werden bewusst uebersprungen.
          if (line.startsWith(":")) continue;
          if (line.startsWith("event:")) eventType = line.slice(6).trim();
          else if (line.startsWith("data:")) dataStr += line.slice(5).trim();
          // `id:` traegt dieselbe Sequenznummer bereits im JSON-Body (`seq`)
          // und wird hier bewusst nicht separat ausgewertet.
        }
        if (!dataStr || (eventType !== "snapshot" && eventType !== "run")) continue;
        try {
          const parsed = JSON.parse(dataStr) as { seq?: unknown; runs?: unknown; run?: unknown };
          if (typeof parsed.seq === "number") {
            yield {
              type: eventType,
              seq: parsed.seq,
              runs: Array.isArray(parsed.runs) ? parsed.runs : undefined,
              run: parsed.run,
            };
          }
        } catch {
          // malformed frame ignorieren
        }
      }
    }
  },
  health: () => jfetch<HealthInfo>("/api/health"),
  buildInfo: () => jfetch<BuildInfo>("/api/build-info"),
  ownerAuthStatus: () => jfetch<OwnerAuthStatusView>("/api/owner/status", { headers: {} }),
  session: () => jfetch<{ ok: boolean; expiresAt: string; user: SimoSessionUser }>("/api/auth/session"),

  // SIMO-WEB-OWNER-LOGIN-1 — Owner-Session im Web etablieren (Mobile nutzt denselben Endpoint).
  login: async (email: string, password: string): Promise<SimoLoginResult> => {
    const res = await jfetch<SimoLoginResult>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    if (res.ok) {
      await api.session();
      markOwnerSessionActive();
    }
    return res;
  },
  logout: async (): Promise<void> => {
    try {
      await jfetch<{ ok: boolean }>("/api/auth/logout", { method: "POST" });
    } catch {
      /* Server-Session evtl. schon weg — Token lokal trotzdem entfernen */
    } finally {
      clearSessionToken();
      clearProxyBasicAuth();
    }
  },
  changePassword: (currentPassword: string, newPassword: string): Promise<SimoPasswordChangeResult> =>
    jfetch<SimoPasswordChangeResult>("/api/auth/change-password", {
      method: "POST",
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
  requestPasswordReset: (email: string): Promise<SimoPasswordResetRequestResult> =>
    jfetch<SimoPasswordResetRequestResult>("/api/auth/password-reset/request", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),

  listSecrets: () => jfetch<{ items: SecretRecordView[] }>("/api/secrets"),
  upsertSecret: async (_input: { key: string; label: string; value: string; connector?: string }): Promise<{ ok: true; record: SecretRecordView }> => {
    throw new Error("renderer_secret_write_blocked");
  },
  removeSecret: (key: string) =>
    jfetch<{ ok: true }>(`/api/secrets/${encodeURIComponent(key)}`, { method: "DELETE" }),

  listPermissions: () => jfetch<{ items: PermissionItem[] }>("/api/permissions"),
  setPermission: (actionId: string, mode: PermissionItem["mode"]) =>
    jfetch<{ ok: true }>("/api/permissions", {
      method: "POST",
      body: JSON.stringify({ actionId, mode }),
    }),

  listAudit: (limit = 100) =>
    jfetch<{ items: AuditEntryView[] }>(`/api/audit?limit=${limit}`),
  verifyAudit: () =>
    jfetch<{ ok: true } | { ok: false; brokenAt: number }>("/api/audit/verify"),

  listConnectors: () =>
    jfetch<{ items: ConnectorStatusView[] }>("/api/connectors"),
  briefing: () => jfetch<CockpitBriefing>("/api/cockpit/briefing"),

  // ── CP-08 — Control Plane Overview (Observability-Endpunkte aus CP-04/07) ──
  controlPlaneSummary: () => jfetch<ControlPlaneSummary>("/api/control-plane/summary"),
  controlPlaneSupervisor: () => jfetch<ControlPlaneSupervisorView>("/api/control-plane/supervisor"),
  controlPlaneQueue: (limit = 8, state?: string, q?: string, offset = 0) =>
    jfetch<QueueResponse>(
      `/api/control-plane/queue?limit=${encodeURIComponent(String(limit))}${state ? `&state=${encodeURIComponent(state)}` : ""}${q ? `&q=${encodeURIComponent(q)}` : ""}&offset=${encodeURIComponent(String(offset))}`,
    ),
  humanGates: (status: "OPEN" | "ALL" = "OPEN") =>
    jfetch<HumanGatesResponse>(`/api/human-gates?status=${status}`),
  gateBundles: (init?: RequestInit) => jfetch<GateBundlesResponse>("/api/human-gates/bundles", init),
  // ── S0 — Owner-Gate (AUTHORITY_ADVANCE): Lesepfad mit Ehrlichkeitszustand
  // sowie Batch-Advance/Halten. Surface-Header "simo" ist Pflicht
  // (AGENT_EXCHANGE_SURFACE_HEADER, fail-closed im Server).
  ownerGateStatus: (init?: RequestInit) =>
    jfetch<OwnerGateStatusResponse>("/api/agent-exchange/owner-gate", {
      headers: { "x-simo-exchange-surface": "simo" },
      ...(init ?? {}),
      signal: init?.signal,
    }),
  authorityGateHold: (reason: string, gateId: string) =>
    jfetch<{ ok: boolean; gate: OwnerGateView }>("/api/agent-exchange/missions/authority/gate-hold", {
      method: "POST",
      headers: { "x-simo-exchange-surface": "simo" },
      body: JSON.stringify({ reason, gateId }),
    }),
  missions: (init?: RequestInit) => jfetch<MissionsListResponse>("/api/missions", init),
  missionDetail: (missionId: string) =>
    jfetch<MissionDetailResponse>(`/api/missions/${encodeURIComponent(missionId)}`),
  agentRoomFeed: (missionId: string, limit = 100) =>
    jfetch<AgentRoomFeedResponse>(`/api/agent-room/feed?missionId=${encodeURIComponent(missionId)}&limit=${limit}`),
  missionCheckpoints: (missionId: string, limit = 50) =>
    jfetch<MissionCheckpointsResponse>(`/api/missions/${encodeURIComponent(missionId)}/checkpoints?limit=${limit}`),
  missionEvidence: (missionId: string, limit = 50) =>
    jfetch<MissionEvidenceResponse>(`/api/control-plane/missions/${encodeURIComponent(missionId)}/evidence?limit=${limit}`),

  microsoftOAuthStart: () =>
    jfetch<{ url: string } | { error: string }>("/oauth/microsoft/start"),
  googleOAuthStart: () =>
    jfetch<{ url: string } | { error: string }>("/oauth/google/start"),

  chat: (input: { message: string; conversationId?: string; model?: string; roleId?: string }) =>
    jfetch<{
      ok: boolean;
      text: string;
      speechText: string;
      iterations: number;
      toolCalls: number;
      conversationId: string;
      debug: AssistantDebugView;
      error?: string;
    }>("/api/chat", {
      method: "POST",
      body: JSON.stringify(input),
    }),

  // ─── AgentGraph HITL: Approval-Center ────────────────────────────────
  listApprovals: () => jfetch<{ approvals: ApprovalView[] }>("/api/approvals"),
  approvalInbox: () => jfetch<ApprovalInboxView>("/api/approval-inbox?limit=30"),
  approvalInboxApprove: (id: string) =>
    jfetch<ApprovalInboxDecisionView>(`/api/approval-inbox/${encodeURIComponent(id)}/approve`, { method: "POST" }),
  // SIMO-FIRST-GOVERNED-LIVE-EXECUTION-1: Passwort-Re-Verify fuer High-Risk-
  // Approvals (write/destructive). Kurzlebiges, session-gebundenes Step-up.
  stepUp: (password: string) =>
    jfetch<{ ok: boolean }>("/api/auth/step-up", { method: "POST", body: JSON.stringify({ password }) }),
  approvalInboxReject: (id: string) =>
    jfetch<ApprovalInboxDecisionView>(`/api/approval-inbox/${encodeURIComponent(id)}/reject`, { method: "POST" }),
  approvalInboxCancel: (id: string) =>
    jfetch<ApprovalInboxDecisionView>(`/api/approval-inbox/${encodeURIComponent(id)}/cancel`, { method: "POST" }),
  approvalInboxExpire: (id: string) =>
    jfetch<ApprovalInboxDecisionView>(`/api/approval-inbox/${encodeURIComponent(id)}/expire`, { method: "POST" }),
  // C-Option: Aktion ablehnen UND Owner-Anweisung zurueck in den Run geben.
  approvalInboxInstruct: (id: string, instruction: string) =>
    jfetch<ApprovalInboxDecisionView & { instructionRecorded: boolean }>(
      `/api/approval-inbox/${encodeURIComponent(id)}/instruct`,
      { method: "POST", body: JSON.stringify({ instruction }) },
    ),
  connectorWriteExecuteStoredApproval: (id: string) =>
    jfetch<ConnectorWriteStoredExecutionView>(
      `/api/connectors/write-approval/${encodeURIComponent(id)}/execute-stored`,
      { method: "POST" },
    ),
  connectorWriteStoredExecutionReadiness: (id: string) =>
    jfetch<ConnectorWriteStoredExecutionReadinessView>(
      `/api/connectors/write-approval/${encodeURIComponent(id)}/execution-readiness`,
    ),
  connectorWriteLiveEvidence: () =>
    jfetch<ConnectorWriteLiveEvidenceView>("/api/connectors/write-approval/live-evidence"),
  agentRegistry: () => jfetch<AgentRegistryView>("/api/agent-registry"),
  agentOsStudio: () => jfetch<AgentOsStudioReadModelView>("/api/agent-os/studio"),
  agentOsHookSyncAttestation: () =>
    jfetch<{ ok: true; receipt: AgentOsHookHostSyncReceipt }>("/api/agent-os/studio/hooks/sync-attestation", { method: "POST", body: JSON.stringify({}) }),
  brainLearningStudio: (space: BrainLearningSpace) =>
    jfetch<BrainLearningStudioView>(`/api/brain/learning-studio?space=${encodeURIComponent(space)}`),
  brainLearningStageCanary: (body: {
    lessonId: string;
    lineageKey: string;
    expiresAt: string;
    idempotencyKey: string;
    targetRoleId?: string;
    proposalId?: string;
  }) => jfetch<{ ok: true; release: BrainLearningReleaseView; canary: Record<string, unknown> }>("/api/brain/learning-releases/canary", {
    method: "POST",
    body: JSON.stringify(body),
  }),
  brainLearningPromote: (id: string, expectedVersion: number, idempotencyKey: string) =>
    jfetch<{ ok: true; release: BrainLearningReleaseView; receipt: Record<string, unknown> }>(`/api/brain/learning-releases/${encodeURIComponent(id)}/promote`, {
      method: "POST",
      body: JSON.stringify({ expectedVersion, idempotencyKey }),
    }),
  brainLearningRollback: (id: string, expectedVersion: number, idempotencyKey: string) =>
    jfetch<{ ok: true; release: BrainLearningReleaseView; receipt: Record<string, unknown> }>(`/api/brain/learning-releases/${encodeURIComponent(id)}/rollback`, {
      method: "POST",
      body: JSON.stringify({ expectedVersion, idempotencyKey }),
    }),
  agentOsCreateDraft: (body: { entityType: AgentOsDraftEntityType; entityId?: string | null; baseHash?: string; payload: Record<string, unknown> }) =>
    jfetch<{ ok: boolean; writesPerformed: false; draft: AgentOsDraftView }>("/api/agent-os/drafts", { method: "POST", body: JSON.stringify(body) }),
  agentOsDraft: (id: string) => jfetch<{ ok: boolean; draft: AgentOsDraftView }>(`/api/agent-os/drafts/${encodeURIComponent(id)}`),
  agentOsDeleteDraft: (id: string) =>
    jfetch<{ ok: boolean; writesPerformed: false; deleted: { id: string; entityType: AgentOsDraftEntityType; entityId: string | null; status: AgentOsDraftStatus } }>(`/api/agent-os/drafts/${encodeURIComponent(id)}`, { method: "DELETE" }),
  agentOsDraftAction: (id: string, action: "validate" | "simulate" | "approve" | "rollback") =>
    jfetch<{ ok: boolean; writesPerformed: boolean; draft: AgentOsDraftView }>(`/api/agent-os/drafts/${encodeURIComponent(id)}/${action}`, { method: "POST", body: JSON.stringify({}) }),
  agentOsApplyDraft: (id: string) =>
    jfetch<{ ok: boolean; writesPerformed: boolean; draft: AgentOsDraftView }>(`/api/agent-os/drafts/${encodeURIComponent(id)}/apply`, { method: "POST", body: JSON.stringify({}) }),
  agentOsDraftAudit: (id: string) =>
    jfetch<{ ok: boolean; audit: { id: number; timestamp: string; actionId?: string; result: string; summary: string; chainVerified: boolean } }>(`/api/agent-os/drafts/${encodeURIComponent(id)}/audit`),
  agentOsSkillTest: (skillId: string, workflow: AgentOsSkillWorkflow) =>
    jfetch<{ ok: true; receipt: AgentOsSkillTestReceipt }>(`/api/agent-os/studio/skills/${encodeURIComponent(skillId)}/test`, { method: "POST", body: JSON.stringify({ workflow }) }),
  agentOsToolPolicyTest: (toolId: string) =>
    jfetch<{ ok: true; receipt: AgentOsToolPolicyTestReceipt }>(`/api/agent-os/studio/tools/${encodeURIComponent(toolId)}/policy-test`, { method: "POST", body: JSON.stringify({}) }),
  agentTeamDelegate: (body: { parentRunId: string; agentId: string; task: string }) =>
    jfetch<{ ok: true; teamId: string; parentRunId: string; childRunId: string; agentId: string; actorId: string; status: "completed" | "aborted" | "failed"; startedAt: string; completedAt: string; harnessRef: string; ledgerEventIds: string[]; graphHash: string; workload: { workloadId: string; provider: null; model: null; toolCalls: readonly string[]; findingsCount: number; taskRef: string } | null; workloadError: "supervisor_readonly_inspection_failed" | null; writesPerformed: true }>("/api/agent-harness/team/delegate", { method: "POST", body: JSON.stringify(body) }),
  agentTeamGraph: (parentRunId: string) =>
    jfetch<{ ok: true; graph: AgentTeamGraphView }>(`/api/agent-harness/team/${encodeURIComponent(parentRunId)}/graph`),
  providerGoldenComparisonReadiness: () =>
    jfetch<ProviderGoldenReadinessView>("/api/agent-harness/provider-comparison/readiness"),
  providerGoldenComparisonExecute: () =>
    jfetch<{ ok: true; parentRunId: string; comparison: ProviderGoldenComparisonView }>("/api/agent-harness/provider-comparison/execute", { method: "POST", body: JSON.stringify({}) }),
  providerGoldenComparison: (parentRunId: string) =>
    jfetch<{ ok: true; comparison: ProviderGoldenComparisonView }>(`/api/agent-harness/provider-comparison/${encodeURIComponent(parentRunId)}`),
  agentHarnessControl: async () => {
    const runId = `ui-harness-readback-${new Date().toISOString().slice(0, 10)}`;
    const goal = "SIMO Harness Control Center Readback";
    const [
      brainContextPacket,
      capabilityEnvelope,
      subagentRegistry,
      handoff,
      supervisorLoop,
      handoffDrift,
      forgeCiDrift,
      connectorReadiness,
      routineHealth,
      uiUxReality,
    ] = await Promise.all([
      jfetch<AgentHarnessControlView["brainContextPacket"]>(
        `/api/agent-harness/brain-context-packet?goal=${encodeURIComponent(goal)}`,
      ),
      jfetch<AgentHarnessControlView["capabilityEnvelope"]>(
        `/api/agent-harness/capability-envelope?runId=${encodeURIComponent(runId)}&agent=simo`,
      ),
      jfetch<AgentHarnessControlView["subagentRegistry"]>("/api/agent-harness/subagent-registry"),
      jfetch<AgentHarnessControlView["handoff"]>(
        `/api/agent-harness/codex-claude-handoff?runId=${encodeURIComponent(runId)}&goal=${encodeURIComponent(goal)}&originatingAgent=codex&targetAgent=claude`,
      ),
      jfetch<AgentHarnessControlView["supervisorLoop"]>("/api/agent-harness/supervisor-loop"),
      jfetch<AgentHarnessControlView["handoffDrift"]>(
        `/api/agent-harness/handoff-drift?runId=${encodeURIComponent(runId)}&goal=${encodeURIComponent(goal)}`,
      ),
      jfetch<AgentHarnessControlView["forgeCiDrift"]>("/api/agent-harness/forge-ci-drift"),
      jfetch<AgentHarnessControlView["connectorReadiness"]>("/api/agent-harness/connector-readiness"),
      jfetch<AgentHarnessControlView["routineHealth"]>("/api/agent-harness/routine-health"),
      jfetch<AgentHarnessControlView["uiUxReality"]>("/api/agent-harness/ui-ux-reality"),
    ]);
    return {
      brainContextPacket,
      capabilityEnvelope,
      subagentRegistry,
      handoff,
      supervisorLoop,
      handoffDrift,
      forgeCiDrift,
      connectorReadiness,
      routineHealth,
      uiUxReality,
    } satisfies AgentHarnessControlView;
  },
  startProjectWorkflow: (body: { routeId: string; goal: string }) =>
    jfetch<ProjectWorkflowStartResult>("/api/agent-registry/project-workflows/start", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  workerSandboxStatus: () => jfetch<WorkerSandboxStatusView>("/api/worker-sandbox/status"),
  runnerJobBoundary: () => jfetch<RunnerJobBoundaryView>("/api/runner/jobs/boundary"),
  approveApproval: async (_id: string): Promise<{ ok: boolean; runId?: string; error?: string }> => {
    throw new Error("legacy_approval_action_blocked");
  },
  rejectApproval: async (_id: string): Promise<{ ok: boolean; runId?: string; error?: string }> => {
    throw new Error("legacy_approval_action_blocked");
  },

  // ─── SIMO-OMNICHANNEL-ACTIONS-B1: Owner-Device-Trust ─────────────────
  listOwnerDevices: () => jfetch<{ devices: OwnerDeviceView[] }>("/api/owner/devices"),
  setOwnerDeviceTrust: (id: string, trusted: boolean) =>
    jfetch<{ ok: boolean; device: OwnerDeviceView }>(
      `/api/owner/devices/${encodeURIComponent(id)}/approval`,
      { method: "PATCH", body: JSON.stringify({ trusted }) },
    ),

  // ─── SIMO-WEB-DESKTOP-PUSH-C1: Browser Web Push ─────────────────────
  getWebPushConfig: () => jfetch<WebPushConfigView>("/api/web-push/config"),
  subscribeWebPush: (subscription: WebPushSubscriptionView) =>
    jfetch<{ ok: boolean }>("/api/web-push/subscriptions", {
      method: "POST",
      body: JSON.stringify(subscription),
    }),
  unsubscribeWebPush: (endpoint: string) =>
    jfetch<{ ok: boolean }>("/api/web-push/subscriptions", {
      method: "DELETE",
      body: JSON.stringify({ endpoint }),
    }),

  // SSE-Streaming-Chat. Returns AsyncIterable über parsed Events.
  // `resumeRunId` setzt einen pausierten (awaiting_approval) Run fort — dann ist
  // `message` nicht nötig (Runtime rehydriert aus der DB).
  chatStream: async function* (input: {
    message?: string;
    conversationId?: string;
    model?: string;
    roleId?: string;
    resumeRunId?: string;
  }, signal?: AbortSignal): AsyncIterable<{ event: string; data: Record<string, unknown> }> {
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    const res = await fetch("/api/chat/stream", {
      method: "POST",
      headers,
      body: JSON.stringify(input),
      signal,
      credentials: "same-origin",
    });
    if (!res.ok || !res.body) {
      const text = await res.text().catch(() => "");
      throw buildOwnerApiError("/api/chat/stream", res, text);
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let idx: number;
      while ((idx = buffer.indexOf("\n\n")) !== -1) {
        const raw = buffer.slice(0, idx).trim();
        buffer = buffer.slice(idx + 2);
        if (!raw) continue;
        const lines = raw.split("\n");
        let ev = "message";
        let dataStr = "";
        for (const l of lines) {
          if (l.startsWith("event:")) ev = l.slice(6).trim();
          else if (l.startsWith("data:")) dataStr += l.slice(5).trim();
        }
        try {
          yield { event: ev, data: dataStr ? JSON.parse(dataStr) : {} };
        } catch {
          // ignore malformed
        }
      }
    }
  },

  // SIMO-COMMAND-CENTER-LIVE-EVENTS-2 — read-only SSE-Live-Stream fürs Dashboard.
  // Liefert `snapshot`-Events (mit overview) bei Aktivitätsänderung + `heartbeat`.
  // GET ohne Body; selbe SSE-Parse-Logik wie chatStream.
  commandStream: async function* (
    signal?: AbortSignal,
  ): AsyncIterable<{ event: string; data: Record<string, unknown> }> {
    const headers: Record<string, string> = { Accept: "text/event-stream" };
    const res = await fetch("/api/command-center/stream", {
      headers,
      signal,
      credentials: "same-origin",
    });
    if (!res.ok || !res.body) {
      const text = await res.text().catch(() => "");
      throw buildOwnerApiError("/api/command-center/stream", res, text);
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let idx: number;
      while ((idx = buffer.indexOf("\n\n")) !== -1) {
        const raw = buffer.slice(0, idx).trim();
        buffer = buffer.slice(idx + 2);
        if (!raw) continue;
        const lines = raw.split("\n");
        let ev = "message";
        let dataStr = "";
        for (const l of lines) {
          if (l.startsWith("event:")) ev = l.slice(6).trim();
          else if (l.startsWith("data:")) dataStr += l.slice(5).trim();
        }
        try {
          yield { event: ev, data: dataStr ? JSON.parse(dataStr) : {} };
        } catch {
          // ignore malformed
        }
      }
    }
  },

  listConversations: () =>
    jfetch<{ items: Array<{ id: string; title: string; updatedAt: string; createdAt: string }> }>(
      "/api/conversations",
    ),
  newConversation: () =>
    jfetch<{ id: string; title: string }>("/api/conversations", { method: "POST" }),
  getConversation: (id: string) =>
    jfetch<{
      id: string;
      title: string;
      messages: Array<{
        id: number;
        role: "user" | "assistant" | "system" | "tool";
        content: string;
        model: string | null;
        iterations: number | null;
        toolCallsJson: string | null;
        createdAt: string;
      }>;
    }>(`/api/conversations/${id}`),
  renameConversation: (id: string, title: string) =>
    jfetch<{ ok: true }>(`/api/conversations/${id}`, {
      method: "PATCH",
      body: JSON.stringify({ title }),
    }),
  deleteConversation: (id: string) =>
    jfetch<{ ok: true }>(`/api/conversations/${id}`, { method: "DELETE" }),

  listVoices: () =>
    jfetch<{
      elevenConfigured: boolean;
      voices: Array<{
        id: "piper" | "brian" | "daniel" | "adam";
        label: string;
        provider: "piper" | "eleven";
        lang: string;
        available?: boolean;
        unavailableReason?: string | null;
        actionLabel?: string;
        fallback?: boolean;
      }>;
    }>("/api/voice/voices"),

  // ─── Phase 8b: Memory-UI ──────────────────────────────────────────────
  listMemory: () =>
    jfetch<{
      currentModel: string;
      total: number;
      entries: Array<{
        key: string;
        value: string;
        createdAt: string;
        updatedAt: string;
        space: MemorySpace;
        kind: MemoryKind;
        sensitivity: MemorySensitivity;
        injectPolicy: MemoryInjectPolicy;
        retention: MemoryRetention;
        reviewStatus: MemoryReviewStatus;
        source: string | null;
        expiresAt: string | null;
        embeddingModel: string | null;
        embeddingStatus: "current" | "stale" | "missing";
      }>;
    }>("/api/memory"),
  // SIMO-DASHBOARD-PANELS-LIVE-2: value-freie Projektion fürs Always-On-
  // Dashboard. Der rohe Memory-`value` geht hier NIE über die Leitung.
  listMemoryMeta: () =>
    jfetch<{
      total: number;
      entries: MemoryMetaEntry[];
    }>("/api/memory/meta"),
  updateMemoryMeta: (key: string, body: Partial<Pick<MemoryMetaEntry, "injectPolicy" | "reviewStatus">>) =>
    jfetch<{ ok: true; memory: Omit<MemoryMetaEntry, "embeddingStatus"> }>(
      `/api/memory/${encodeURIComponent(key)}/meta`,
      { method: "PATCH", body: JSON.stringify(body) },
    ),
  deleteMemory: (key: string) =>
    jfetch<{ ok: true; key: string }>(`/api/memory/${encodeURIComponent(key)}`, {
      method: "DELETE",
    }),
  backfillMemoryEmbeddings: (limit?: number) =>
    jfetch<{
      ok: number;
      failed: number;
      aborted: boolean;
      processed: number;
      pendingRemaining: number;
      model: string;
      errors: Array<{ key: string; error: string }>;
    }>("/api/memory/backfill-embeddings", {
      method: "POST",
      body: JSON.stringify(limit !== undefined ? { limit } : {}),
    }),
  memoryExportUrl: brainExportUrl,
  downloadBrainExport: (options?: BrainExportDownloadOptions) => downloadJson(brainExportUrl(options)),
  validateBrainRestore: (payload: unknown, space = "all") =>
    jfetch<BrainRestoreValidateResult>(`/api/memory/restore/validate?space=${encodeURIComponent(space)}`, {
      method: "POST",
      body: JSON.stringify(payload),
    }),
  listBrainDecisionLog: (limit = 20) =>
    jfetch<{ items: BrainDecisionLogItem[] }>(`/api/brain/decision-log?limit=${encodeURIComponent(String(limit))}`),

  // ─── Phase 7: Routinen ───────────────────────────────────────────────
  listRoutines: () =>
    jfetch<{
      items: RoutineView[];
      // Ehrliches Scheduler-Signal: true nur, wenn der Routine-Scheduler
      // serverseitig laeuft. Aeltere Server ohne das Feld => undefined; die UI
      // behandelt fehlend wie "aus" (ehrlicher Prod-Default).
      schedulerEnabled?: boolean;
    }>("/api/routines"),
  listRoutineRuns: (limit = 20) =>
    jfetch<{
      items: RoutineRunView[];
    }>(`/api/routines/runs?limit=${limit}`),
  createRoutine: (input: {
    label: string;
    prompt: string;
    triggerConfig: Record<string, unknown>;
    enabled?: boolean;
  }) =>
    jfetch<{ ok: true; id: string } | { error: string; details?: unknown }>("/api/routines", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  updateRoutine: (
    id: string,
    patch: {
      label?: string;
      prompt?: string;
      triggerConfig?: Record<string, unknown>;
      enabled?: boolean;
    },
  ) =>
    jfetch<{ ok: true } | { error: string }>(`/api/routines/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: JSON.stringify(patch),
    }),
  removeRoutine: (id: string) =>
    jfetch<{ ok: true }>(`/api/routines/${encodeURIComponent(id)}`, { method: "DELETE" }),
  runRoutineNow: (id: string) =>
    jfetch<{ ok: true; runId: number } | { error: string }>(
      `/api/routines/${encodeURIComponent(id)}/run`,
      { method: "POST" },
    ),
  agentOsLoopRun: (loopId: string) =>
    jfetch<{ ok: true; receipt: AgentOsLoopExecutionReceipt }>(
      `/api/agent-os/studio/loops/${encodeURIComponent(loopId)}/run`,
      { method: "POST", body: JSON.stringify({}) },
    ),
  agentOsLoopDryRun: (loopId: string) =>
    jfetch<{ ok: true; receipt: AgentOsLoopExecutionReceipt }>(
      `/api/agent-os/studio/loops/${encodeURIComponent(loopId)}/dry-run`,
      { method: "POST", body: JSON.stringify({}) },
    ),
  agentOsLoopApproveAndResume: (runId: string, approvalId: string) =>
    jfetch<{ ok: true; receipt: AgentOsLoopExecutionReceipt }>(
      `/api/agent-os/studio/loops/runs/${encodeURIComponent(runId)}/approve-and-resume`,
      { method: "POST", body: JSON.stringify({ approvalId }) },
    ),
  agentOsLoopRecover: (runId: string, tokenBudgetOverride?: number) =>
    jfetch<{ ok: true; receipt: AgentOsLoopExecutionReceipt }>(
      `/api/agent-os/studio/loops/runs/${encodeURIComponent(runId)}/recover`,
      { method: "POST", body: JSON.stringify(tokenBudgetOverride === undefined ? {} : { tokenBudgetOverride }) },
    ),
  toggleRoutine: (id: string, enabled: boolean) =>
    jfetch<{ ok: true; enabled: boolean }>(
      `/api/routines/${encodeURIComponent(id)}/toggle`,
      { method: "POST", body: JSON.stringify({ enabled }) },
    ),
  agentRoutineLifecycleStart: (target = "routine-health") =>
    jfetch<{
      ok: true;
      runId: string;
      action: "dry_run";
      phase: "dry_run";
      status: "running";
      ledgerEventId: string;
      writesPerformed: true;
      redactionStatus: "secrets-omitted";
      summary: Record<string, number>;
    }>("/api/agent-harness/routine-lifecycle/start", {
      method: "POST",
      body: JSON.stringify({ target }),
    }),
  agentRoutineLifecycleAction: (runId: string, action: "execute" | "pause" | "resume" | "stop" | "rollback") =>
    jfetch<{
      ok: true;
      runId: string;
      action: "execute" | "pause" | "resume" | "stop" | "rollback";
      phase: "dry_run" | "execute" | "awaiting_approval" | "cancelled";
      status: "running" | "awaiting_approval" | "cancelled";
      ledgerEventId: string;
      writesPerformed: true;
      rollbackRef?: string;
    }>(`/api/agent-harness/routine-lifecycle/${encodeURIComponent(runId)}/${action}`, {
      method: "POST",
      body: JSON.stringify({}),
    }),
  agentOrbMissionStart: (goal: string) =>
    jfetch<
      | { ok: true; mission: OwnerMissionView; queue: { workItemId: string; missionId: string; queueState: string }; waitReason: string | null }
      | { ok: false; error: string; reason?: string }
    >("/api/agent-harness/orb-mission/start", {
      method: "POST",
      body: JSON.stringify({ goal }),
    }),
  agentOrbMissionCurrent: () =>
    jfetch<{ ok: true; mission: OwnerMissionView | null }>("/api/agent-harness/orb-mission/current"),
  agentOrbMissionRead: (runId: string) =>
    jfetch<{ ok: true; mission: OwnerMissionView }>(`/api/agent-harness/orb-mission/${encodeURIComponent(runId)}`),
  agentOrbMissionAction: (runId: string, action: "pause" | "resume" | "stop") =>
    jfetch<{ ok: true; mission: OwnerMissionView }>(`/api/agent-harness/orb-mission/${encodeURIComponent(runId)}/${action}`, {
      method: "POST",
      body: JSON.stringify({}),
    }),

  // Phase 4 Slice D — Codex Worktree-Review
  listWorktrees: () =>
    jfetch<{
      items: Array<{
        id: string;
        branch: string;
        baseRef: string;
        path: string;
        createdAt: string;
        expiresAt: string;
      }>;
    }>("/api/worktree"),
  getWorktreeDiff: (id: string) =>
    jfetch<{ diff: string; commit: string; baseRef: string }>(
      `/api/worktree/${encodeURIComponent(id)}/diff`,
    ),
  removeWorktree: (id: string) =>
    jfetch<{ ok: true } | { error: string; hint?: string }>(
      `/api/worktree/${encodeURIComponent(id)}`,
      { method: "DELETE" },
    ),
  // Slice E
  createWorktree: (body: { branch: string; baseRef?: string }) =>
    jfetch<
      | { id: string; branch: string; baseRef: string; path: string; createdAt: string; expiresAt: string }
      | { error: string; details?: unknown }
    >("/api/worktree/create", { method: "POST", body: JSON.stringify(body) }),
  startCodeWorkspace: (body: {
    goal: string;
    branch: string;
    baseRef?: string;
    files?: string[];
    testCommands?: string[];
    agentMode?: string;
  }) =>
    jfetch<
      | {
          ok: true;
          mode: "code_workspace_session";
          policyId: string;
          requestId: string;
          runId: string;
          conversationId: string;
          executionAllowed: false;
          noSilentExecution: true;
          sourceRepo: { path: string; viaEnv: boolean } | null;
          worktreeRoot: string | null;
          worktree: {
            id: string;
            branch: string;
            baseRef: string;
            path: string;
            createdAt: string;
            expiresAt: string;
          };
          plan: {
            goal: string;
            branch: string;
            baseRef: string;
            agentMode: string;
            files: string[];
            testCommands: string[];
            nextSmoke: string;
            stages: Array<{ id: string; title: string; status: string; evidenceRequired: boolean }>;
          };
          diff: {
            raw: string;
            commit: string;
            baseRef: string;
            files: Array<{ path: string; additions: number; deletions: number }>;
            prepared: true;
            empty: boolean;
          };
          runEvents: Array<{ id: string; ts: string; level: string; message: string }>;
          approval: {
            id: string;
            status: string;
            runId: string;
            worktreeId: string;
            requiredDecisionBy: string;
            executeOnApproval: false;
          };
          evidence: {
            id: number;
            auditId: number;
            evidenceRef: string;
            approvalId: string;
            requestId: string;
            runId: string;
            worktreeId: string;
          };
          risks: string[];
          gates: Record<string, string>;
          boundary: Record<string, boolean | null>;
        }
      | { ok: false; error: unknown; requestId: string; runId?: string; executionAllowed: false }
    >("/api/worktree/code-workspace/start", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  planCodexCodeWorkflow: (body: {
    goal: string;
    branch: string;
    baseRef?: string;
    files: string[];
    testCommands: string[];
    prTitle: string;
    prBody: string;
  }) =>
    jfetch<
      | {
          ok: true;
          mode: "codex_like_code_workflow_plan";
          policyId: string;
          requestId: string;
          runId: string;
          conversationId: string;
          executionAllowed: false;
          noSilentExecution: true;
          workflow: {
            goal: string;
            branch: string;
            baseRef: string;
            files: string[];
            testCommands: string[];
            prTitle: string;
            prBody: string;
            stages: Array<{ id: string; title: string; status: string; executionAllowed: false; evidenceRequired: boolean }>;
          };
          approval: { id: string; status: string; requiredDecisionBy: string; executeOnApproval: false };
          evidence: { id: number; auditId: number; evidenceRef: string; approvalId: string };
          gates: Record<string, string>;
          boundary: Record<string, boolean | null>;
        }
      | { ok: false; error: unknown; requestId: string; executionAllowed: false }
    >("/api/worktree/codex-code-workflow/plan", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  planControlledGitAutomation: (body: {
    branch: string;
    baseRef?: string;
    commitMessage: string;
    changes: Array<{ path: string; content: string }>;
    prTitle: string;
    prBody: string;
  }) =>
    jfetch<
      | {
          ok: true;
          mode: "controlled_git_automation_plan";
          policyId: string;
          requestId: string;
          runId: string;
          conversationId: string;
          executionAllowed: false;
          approval: { id: string; status: string; requiredDecisionBy: string; executeOnApproval: false };
          preview: {
            branchPlan: { branch: string; baseRef: string };
            commitPlan: { message: string; files: string[] };
            prPlan: { title: string; body: string; commandPreview: string };
          };
          evidence: { id: number; auditId: number; approvalId: string };
          boundary: {
            approvedExecuteEndpoint: string;
            pushAllowed: false;
            prCreateAllowed: false;
            mergeAllowed: false;
            deployAllowed: false;
          };
        }
      | { ok: false; error: unknown; requestId: string; executionAllowed: false }
    >("/api/worktree/controlled-git-automation/plan", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  executeApprovedControlledGitAutomation: (body: {
    approvalId: string;
    branch: string;
    baseRef?: string;
    commitMessage: string;
    changes: Array<{ path: string; content: string }>;
    prTitle: string;
    prBody: string;
  }) =>
    jfetch<
      | {
          ok: true;
          status: "executed";
          requestId: string;
          policyId: string;
          executionAllowed: true;
          evidence: { id: number; auditId: number; approvalId: string; runId: string };
          result: {
            worktreeId: string;
            branch: string;
            baseRef: string;
            commit: string;
            filesWritten: string[];
            prPlan: {
              title: string;
              body: string;
              baseRef: string;
              headRef: string;
              commandPreview: string;
              executionMode: "manual_repository_governance";
            };
          };
          boundary: {
            branchCreateAllowed: true;
            commitAllowed: true;
            pushAllowed: false;
            prCreateAllowed: false;
            mergeAllowed: false;
            deployAllowed: false;
          };
        }
      | { ok: false; reason: string; requestId: string; executionAllowed: false; deploy: false }
    >("/api/worktree/controlled-git-automation/approved-execute", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  listCodexCodeWorkflowSessions: () =>
    jfetch<{
      ok: true;
      mode: "codex_like_code_workflow_sessions";
      policyId: string;
      executionAllowed: false;
      sessions: CodexCodeWorkflowSessionView[];
    }>("/api/worktree/codex-code-workflow/sessions"),
  planControlledShell: (body: { command: string; reason: string; expectedOutput: string }) =>
    jfetch<ControlledShellPlanView | { ok: false; error: string; requestId: string; executionAllowed: false }>(
      "/api/tools/controlled-shell/plan",
      {
        method: "POST",
        body: JSON.stringify(body),
      },
    ),
  applyWorktreeToSource: (
    id: string,
    body: { commitMessage: string; selectedFiles: string[] },
  ) =>
    jfetch<
      | {
          ok: true;
          commit: string;
          appliedFiles: string[];
          preHeadSha: string;
          postHeadSha: string;
        }
      | { error: string; hint?: string }
    >(`/api/worktree/${encodeURIComponent(id)}/apply-to-source`, {
      method: "POST",
      body: JSON.stringify(body),
    }),

  // ─── SIMO-COMMAND-CENTER-READONLY-1: read-only Aggregat-Endpunkte ─────
  commandOverview: () => jfetch<CommandOverview>("/api/command-center/overview"),
  commandRuns: (opts?: { limit?: number; status?: string }) =>
    jfetch<{ runs: CommandRun[] }>(
      `/api/command-center/runs?limit=${opts?.limit ?? 20}${opts?.status ? `&status=${encodeURIComponent(opts.status)}` : ""}`,
    ),
  commandRun: (id: string) =>
    jfetch<{ run: CommandRun; tools: CommandToolRun[] }>(`/api/command-center/runs/${encodeURIComponent(id)}`),
  commandTools: (limit = 20) =>
    jfetch<{ tools: CommandToolRun[] }>(`/api/command-center/tools?limit=${encodeURIComponent(String(limit))}`),
  commandTimeline: (limit = 40) =>
    jfetch<{ events: CommandTimelineEvent[] }>(`/api/command-center/timeline?limit=${limit}`),
  commandAgents: () => jfetch<{ agents: CommandAgent[] }>("/api/command-center/agents"),
  commandInfra: () => jfetch<CommandInfra>("/api/command-center/infra"),
  runtimeReliabilityBaseline: () => jfetch<RuntimeReliabilityBaseline>("/api/runtime/reliability-baseline"),
  listNotifications: () =>
    jfetch<{ notifications: NotificationView[]; centerItems?: NotificationCenterItemView[] }>("/api/notifications?limit=20"),
  markNotification: (id: string, status: "unread" | "read" | "archived") =>
    jfetch<{ ok: boolean; notification?: NotificationView }>(`/api/notifications/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify({ status }),
    }),
  replyNotification: (body: {
    correlationId: string;
    channel: NotificationReplyChannel;
    action: NotificationReplyAction;
    idempotencyKey: string;
    deviceId?: string;
    conversationId?: string;
    payload?: Record<string, unknown>;
  }) =>
    jfetch<{ ok: boolean; duplicate?: boolean; error?: string }>("/api/notifications/replies", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  listLearningProposals: () =>
    jfetch<{ proposals: LearningProposalView[] }>("/api/learning/proposals"),
  createLearningProposal: (body: {
    kind: LearningProposalKind;
    targetKey: string;
    proposedValue: string;
    source: string;
    reason: string;
    evidence?: unknown;
  }) =>
    jfetch<{ ok: boolean; proposal?: LearningProposalView; error?: string }>("/api/learning/proposals", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  createWorkQueueIdea: (body: {
    title: string;
    goal: string;
    productScope?: string;
    priority?: AutonomousWorkQueuePriority;
    assignedAgent?: string;
  }) =>
    jfetch<{ ok: boolean; item?: { id: string } }>("/api/mobile/work-queue", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  listWorkQueue: (limit = 20) =>
    jfetch<{ ok: boolean; items: AutonomousWorkQueueItemView[] }>(`/api/work-queue?limit=${encodeURIComponent(String(limit))}`),
  updateWorkQueueItem: (
    id: string,
    body: {
      priority?: AutonomousWorkQueuePriority;
      status?: AutonomousWorkQueueStatus;
      assignedAgent?: string;
      approvalMode?: AutonomousWorkQueueApprovalMode;
      dueAt?: string | null;
      reviewAt?: string | null;
      blockedReason?: string | null;
    },
  ) =>
    jfetch<{ ok: boolean; item?: AutonomousWorkQueueItemView }>(`/api/work-queue/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  createRoutineFromWorkQueueItem: (id: string) =>
    jfetch<{
      ok: boolean;
      routine?: {
        id: string;
        label: string;
        triggerType: "schedule" | "event" | "threshold";
        triggerConfigJson: string;
        prompt: string;
        enabled: boolean;
        createdAt: string;
        updatedAt: string;
      };
      item?: AutonomousWorkQueueItemView | null;
      error?: string;
    }>(`/api/work-queue/${encodeURIComponent(id)}/routine`, { method: "POST" }),
  decideLearningProposal: (id: string, decision: "reject") =>
    jfetch<{ ok: boolean; proposal?: LearningProposalView; error?: string }>(
      `/api/learning/proposals/${encodeURIComponent(id)}/decide`,
      { method: "POST", body: JSON.stringify({ decision }) },
    ),
  listAgentBrainScopes: () =>
    jfetch<{ ok: boolean; scopes: AgentBrainScopeEntryView[] }>("/api/brain/agent-scope"),
  setAgentBrainScope: (
    roleId: string,
    body: { allowedSpaces: AgentBrainScopeSpace[]; allowedKinds?: string[]; maxSensitivity: AgentBrainScopeSensitivity },
  ) =>
    jfetch<{ ok: boolean; scope: AgentBrainScopeView }>(`/api/brain/agent-scope/${encodeURIComponent(roleId)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),

  // ── SIMO-AGENT-REGISTRY-3 Slice 1 — Rollen-Registry CRUD ──────────────
  listRoles: () => jfetch<{ roles: Role[] }>("/api/command-center/roles"),
  createRole: (body: RoleCreate) =>
    jfetch<{ role: Role }>("/api/command-center/roles", { method: "POST", body: JSON.stringify(body) }),
  updateRole: (id: string, body: RoleUpdate) =>
    jfetch<{ role: Role }>(`/api/command-center/roles/${encodeURIComponent(id)}`, {
      method: "PATCH",
      body: JSON.stringify(body),
    }),
  deleteRole: (id: string) =>
    jfetch<{ ok: true }>(`/api/command-center/roles/${encodeURIComponent(id)}`, { method: "DELETE" }),

  // ── SIMO-GATED-ACTIONS-5 Slice 1 — Armed-Gate ─────────────────────────
  listArmed: () => jfetch<{ actions: ArmedActionView[] }>("/api/command-center/armed"),
  armAction: (body: { actionId: string; reason: string; expiresAt?: string; resourcePattern?: string }) =>
    jfetch<{ armed: unknown }>("/api/command-center/armed", { method: "POST", body: JSON.stringify(body) }),
  disarmAction: (actionId: string) =>
    jfetch<{ ok: true; removed: boolean }>(`/api/command-center/armed/${encodeURIComponent(actionId)}`, { method: "DELETE" }),
  listArmedEvidence: (limit = 50) =>
    jfetch<{ evidence: GatedEvidenceView[] }>(`/api/command-center/armed/evidence?limit=${limit}`),

  // ── SIMO-OWNER-RUN-CONSOLE-1 — Codex/Claude-Provider-Lane fuer Owner ohne API-Kenntnisse ──
  providerLaneRuns: () => jfetch<{ ok: boolean; runs: unknown[] }>("/api/provider-lane/runs"),
  providerLaneHumanStatus: (runId: string, level: string) =>
    jfetch<{ ok: boolean; level: string; humanStatus: Record<string, unknown> }>(
      `/api/provider-lane/runs/${encodeURIComponent(runId)}/human-status?level=${encodeURIComponent(level)}`,
    ),
  providerLaneStartRun: (input: { actor: string; goal: string }) =>
    jfetch<{ ok: boolean; run?: { runId: string }; error?: string }>("/api/provider-lane/runs", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  providerLaneApprove: (runId: string, operation: string) =>
    jfetch<{ ok: boolean; operation?: string; approved?: boolean; error?: string }>(
      `/api/provider-lane/runs/${encodeURIComponent(runId)}/operations/${encodeURIComponent(operation)}/approve`,
      { method: "POST" },
    ),
  providerLaneExecute: (runId: string, operation: string) =>
    jfetch<{ ok: boolean; operation?: string; error?: string }>(
      `/api/provider-lane/runs/${encodeURIComponent(runId)}/operations/${encodeURIComponent(operation)}/execute`,
      { method: "POST" },
    ),
  providerLaneStop: (runId: string, reason: string) =>
    jfetch<{ ok: boolean; run?: unknown; error?: string }>(`/api/provider-lane/runs/${encodeURIComponent(runId)}/stop`, {
      method: "POST",
      body: JSON.stringify({ reason }),
    }),
  providerLaneFailover: (runId: string, actor: string) =>
    jfetch<{ ok: boolean; nextRunId?: string; error?: string }>(`/api/provider-lane/runs/${encodeURIComponent(runId)}/failover`, {
      method: "POST",
      body: JSON.stringify({ actor }),
    }),
  // SIMO-FORGEJO-PR-STATE-SYNC-1 — reiner Lese-Abgleich mit Forgejo; die
  // Antwort ist immer 200, `synced` unterscheidet Erfolg von Nichterreichbarkeit.
  providerLanePullRequestSync: (runId: string) =>
    jfetch<{
      synced: boolean;
      reason?: string;
      state: string | null;
      checkedAt: string | null;
      pullRequestNumber: number | null;
      pullRequestUrl: string | null;
    }>(`/api/provider-lane/runs/${encodeURIComponent(runId)}/pull-request/sync`, { method: "POST" }),

  // ── SIMO-MISSION-PLAN-REVIEW-AND-APPROVAL-1 (B01 Slice 3) ──────────────
  missionPlans: () => jfetch<{ ok: boolean; missions: MissionSummaryView[] }>("/api/missions"),
  missionPlanReview: (missionId: string) =>
    jfetch<{ ok: boolean; review: MissionPlanReviewView }>(`/api/missions/${encodeURIComponent(missionId)}/plan`),

  /**
   * SIMO-MISSION-OWNER-ACCESS-1 (B01 Slice 6, Luecke 1) — "freier Zieltext
   * rein, Mission raus, danach Plan erzeugen". Bewusst NICHT ueber `jfetch`:
   * ein abgelehnter Zieltext (400) ist ein erwartetes, im Formular direkt
   * darzustellendes Ergebnis, kein technischer Fehler.
   */
  missionCreate: async (
    goal: string,
    opts: { scope?: string; outOfScope?: string } = {},
  ): Promise<{ ok: true; mission: MissionSummaryView; plan: { alreadyPlanned: boolean; stepCount: number } } | { ok: false; status: number; error: string }> => {
    const res = await fetch("/api/missions", {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ goal, ...(opts.scope ? { scope: opts.scope } : {}), ...(opts.outOfScope ? { outOfScope: opts.outOfScope } : {}) }),
    });
    const body = (await res.json().catch(() => null)) as
      | { ok?: boolean; mission?: MissionSummaryView; plan?: { alreadyPlanned: boolean; stepCount: number }; error?: string }
      | null;
    if (res.ok && body?.ok && body.mission && body.plan) return { ok: true, mission: body.mission, plan: body.plan };
    return { ok: false, status: res.status, error: body?.error ?? "mission_create_failed" };
  },

  /**
   * SIMO-MISSION-OWNER-ACCESS-1 (B01 Slice 6, Luecke 3) — EIN Stop-Aufruf,
   * der serverseitig selbst den richtigen der zwei bestehenden Stop-Pfade
   * waehlt (siehe `apps/server/src/mission-stop.ts`). Bewusst NICHT ueber
   * `jfetch`, gleiches Muster wie `missionPlanDecide`.
   */
  missionStop: async (
    missionId: string,
    expectedVersion: number,
  ): Promise<
    | { ok: true; mission: MissionSummaryView; path: "pre_approval" | "post_approval"; stoppedStepIds: string[] }
    | { ok: false; status: number; error: string; message?: string; mission?: MissionSummaryView }
  > => {
    const res = await fetch(`/api/missions/${encodeURIComponent(missionId)}/stop`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ expectedVersion }),
    });
    const body = (await res.json().catch(() => null)) as
      | { ok?: boolean; mission?: MissionSummaryView; path?: "pre_approval" | "post_approval"; stoppedStepIds?: string[]; error?: string; message?: string }
      | null;
    if (res.ok && body?.ok && body.mission && body.path) {
      return { ok: true, mission: body.mission, path: body.path, stoppedStepIds: body.stoppedStepIds ?? [] };
    }
    return { ok: false, status: res.status, error: body?.error ?? "mission_stop_failed", message: body?.message, mission: body?.mission };
  },
  /**
   * Bewusst NICHT ueber `jfetch`: eine Entscheidung, die als `409` mit dem
   * aktualisierten Missionsstand abgelehnt wird, ist kein technischer Fehler,
   * sondern ein erwartetes, im UI direkt darzustellendes Ergebnis (siehe
   * `apps/server/src/mission-plan-approval.ts`: "genau einmal entscheidbar").
   * `jfetch` wirft bei jedem `!res.ok` und verliert dabei den strukturierten
   * Body — hier bleibt der komplette Server-Body (inkl. `mission`) erhalten,
   * unabhaengig vom Statuscode.
   */
  missionPlanDecide: async (
    missionId: string,
    decision: MissionPlanDecisionKind,
    expectedVersion: number,
    note?: string,
  ): Promise<MissionPlanDecisionOutcome> => {
    const res = await fetch(`/api/missions/${encodeURIComponent(missionId)}/plan/decision`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ decision, expectedVersion, ...(note && note.trim() ? { note: note.trim() } : {}) }),
    });
    const body = (await res.json().catch(() => null)) as
      | { ok?: boolean; mission?: MissionSummaryView; error?: string; message?: string }
      | null;
    if (res.ok && body?.ok && body.mission) return { ok: true, mission: body.mission };
    return {
      ok: false,
      status: res.status,
      error: body?.error ?? "mission_plan_decision_failed",
      message: body?.message,
      mission: body?.mission,
    };
  },

  // ── SIMO-MISSION-PROGRESS-PROJECTION-1 (B01 Slice 5) — rein lesend ──────
  missionProgress: (missionId: string) =>
    jfetch<{ ok: boolean; progress: MissionProgressView }>(`/api/missions/${encodeURIComponent(missionId)}/progress`),
};

// ─── SIMO-GATED-ACTIONS-5 — Armed-Gate-Typen (Slice 1 + 2a/2b) ────────────
// `out_of_scope` (2b): pro-Invocation, wenn resource_pattern die Resource nicht
// deckt. In der Registry-Liste (GET /armed) kommt es NICHT vor (dort Existenz+
// Expiry), aber in Evidence-Records (armedState) sehr wohl.
export type ArmedState = "armed" | "expired" | "not_armed" | "out_of_scope";
export interface ArmedActionView {
  actionId: string;
  label: string;
  connector: string;
  kind: string;
  defaultMode: string;
  armedState: ArmedState;
  reason: string | null;
  armedBy: string | null;
  armedAt: string | null;
  expiresAt: string | null;
  resourcePattern: string | null;
}
export interface GatedEvidenceView {
  id: number;
  actionId: string;
  riskLevel: string;
  runId: string | null;
  approvalId: string | null;
  armedState: string;
  permissionMode: string | null;
  resultStatus: string;
  summary: string | null;
  actor: string | null;
  auditId: number | null;
  createdAt: string;
}

// ─── Command-Center-Typen (SIMO-COMMAND-CENTER-READONLY-1) ──────────────
export interface CommandOverview {
  runs: { total: number; active: number; error: number };
  tools: { total: number };
  approvals: { pending: number };
  connectors: { reachable: number; total: number };
  audit: { ok: boolean };
  memory: { count: number };
  routines: { count: number; runs: number };
  generatedAt: string;
}
export type RuntimeReliabilitySignalStatus = "ok" | "degraded" | "down" | "not_measured";
export interface RuntimeReliabilitySignal {
  id: string;
  label: string;
  status: RuntimeReliabilitySignalStatus;
  value: string;
  detail: string;
  measured: boolean;
}
export interface RuntimeReliabilityBaseline {
  schema: "simo.runtime-reliability-baseline.v1";
  generatedAt: string;
  window: { recentRuns: number; recentTools: number };
  modelLatency: {
    measured: boolean;
    averageMs: number | null;
    lastMs: number | null;
    samples: number;
    source: string;
  };
  errorRate: {
    runs: { total: number; errors: number; rate: number };
    tools: { total: number; errors: number; blocked: number; pending: number; rate: number };
  };
  timeoutSignals: {
    ollamaTimeouts: number;
    toolTimeouts: number;
    source: string;
  };
  signals: RuntimeReliabilitySignal[];
  boundaries: {
    readOnly: true;
    noNewWriteCapability: true;
    noShellGitFilesystemSecretAccess: true;
  };
}
export interface CommandRun {
  id: string;
  conversationId: string;
  triggeredBy: string;
  runtime: string;
  model: string | null;
  roleId: string | null;
  assistantMode: string | null;
  phase: string;
  status: "running" | "awaiting_approval" | "done" | "error" | "cancelled";
  request: string;
  // finalAnswer wird bewusst NICHT exponiert (Wire-Härtung, nie gerendert) —
  // siehe SIMO-RUNS-FINALANSWER-WIRE-HARDENING-1.
  createdAt: string;
  updatedAt: string;
}
export interface CommandToolRun {
  id: number;
  runId: string;
  toolId: string;
  status: string;
  riskLevel: string;
  durationMs: number | null;
  createdAt: string;
}
export interface CommandTimelineEvent {
  id: string;
  at: string;
  type: "run" | "tool" | "approval" | "audit" | "evidence";
  runId: string | null;
  requestId: string | null;
  approvalId: string | null;
  auditId: number | null;
  evidenceRef: string | null;
  agentId: string | null;
  actionId: string | null;
  capability: string | null;
  status: string | null;
  summary: string;
}
export interface CommandAgentTool {
  brainName: string;
  toolId: string;
  name: string;
  permissionKey: string;
  capability: string;
  riskLevel: string;
}
export type RolePermissionFloor = "none" | "draft" | "read_only";
export interface CommandAgent {
  id: string;
  name: string;
  purpose: string;
  enabled: boolean;
  isBuiltin: boolean;
  permissionFloor: RolePermissionFloor;
  assistantModes: string[];
  toolGroups: string[];
  tools: CommandAgentTool[];
  highestRiskLevel: string;
  accessLevel: string;
  liveStatus: "idle" | "active" | "blocked" | "error";
  activeRunIds: string[];
}

// ── SIMO-AGENT-REGISTRY-3 Slice 1 — Rollen-Registry ──────────────────────
export interface Role {
  id: string;
  name: string;
  purpose: string;
  enabled: boolean;
  toolGroups: string[];
  assistantModes: string[];
  permissionFloor: RolePermissionFloor;
  isBuiltin: boolean;
  createdAt: string;
  updatedAt: string;
}
export interface RoleCreate {
  name: string;
  purpose?: string;
  enabled?: boolean;
  toolGroups: string[];
  assistantModes: string[];
  permissionFloor?: RolePermissionFloor;
}
export interface RoleUpdate {
  name?: string;
  purpose?: string;
  enabled?: boolean;
  toolGroups?: string[];
  assistantModes?: string[];
  permissionFloor?: RolePermissionFloor;
}
export interface CommandInfraProbe {
  id: string;
  name: string;
  url: string | null;
  configured: boolean;
  reachable: boolean;
  status: "ok" | "degraded" | "down" | "not_configured";
  httpStatus?: number;
  latencyMs?: number;
  message?: string;
}
export interface CommandTailnetHost {
  id: string;
  hostname: string;
  tailnetName: string;
  role: string;
  location: string;
  serverType: string;
  publicIp: string;
  privateIp: string | null;
  tailscaleIp: string;
  privateNetworkMember: boolean;
}
export interface CommandTailnetInventory {
  id: "tailnet-inventory";
  name: string;
  source: string;
  total: number;
  tailscaleHosts: number;
  privateNetworkHosts: number;
  privateNetworkCidr: string;
  hosts: CommandTailnetHost[];
}
export interface CommandForgejoSignal {
  id: "forgejo";
  name: string;
  role: "primary_git_truth";
  baseUrl: string;
  repo: string;
  defaultBranch: string;
  configured: boolean;
  reachable: boolean;
  status: "ok" | "auth_required" | "degraded" | "down";
  privateRepo?: boolean;
  openPullRequests?: number;
  updatedAt?: string;
  htmlUrl?: string;
  message?: string;
  mirror: { id: "github"; role: "mirror_only"; canonical: false; remoteName: "github" };
}
export interface CommandSimoCiHeadSignal {
  id: "simo-ci-head";
  name: string;
  role: "head_aware_ci_monitor";
  baseUrl: string;
  repo: string;
  branch: string;
  configured: boolean;
  reachable: boolean;
  status: "up" | "warn" | "down";
  headSha: string | null;
  runId: number | null;
  runNumber: number | null;
  runStatus: "success" | "failure" | "cancelled" | "skipped" | "running" | "waiting" | "queued" | "pending" | "requested" | "missing" | "unknown";
  runEvent: string | null;
  runBranch: string | null;
  runUpdatedAt: string | null;
  runAgeSeconds: number | null;
  queueLength: number;
  queueWaiting: number;
  queueRunning: number;
  oldestQueuedAgeSeconds: number | null;
  staleAfterMinutes: number;
  pendingDownAfterMinutes: number;
  runningDownAfterMinutes: number;
  stale: boolean;
  timedOut: boolean;
  exactHeadMatch: boolean;
  tokensInPayload: false;
  message: string;
}
export interface CommandKumaSignal {
  id: "uptime-kuma";
  name: string;
  baseUrl: string;
  statusPageSlug: string;
  configured: boolean;
  reachable: boolean;
  status: "ok" | "degraded" | "down";
  publicMonitors: number;
  upMonitors: number;
  downMonitors: number;
  degradedMonitors: number;
  message?: string;
}
export interface CommandBackupRestoreSignal {
  id: "backup-restore";
  name: string;
  host: string;
  status: "ok" | "conditional" | "down";
  configured: boolean;
  reachable: boolean;
  localBackupTimer: "active" | "inactive" | "unknown";
  backupMonitorTimer: "active" | "inactive" | "unknown";
  offsiteBackupTimer: "active" | "inactive" | "unknown";
  restoreTestMode: "test_path_only" | "unknown";
  liveRestoreOwnerOnly: boolean;
  secretsInEvidence: boolean;
  evidenceRef: string;
  runbookRefs: string[];
  blockers: string[];
  restoreRehearsal: {
    status: "fresh" | "stale" | "missing";
    lastProofAt: string | null;
    proofAgeHours: number | null;
    staleAfterHours: number;
    evidenceRefs: string[];
    missingProofs: string[];
    staleProofs: string[];
    ownerActionRequired: boolean;
    noLiveRestoreAction: boolean;
    message: string;
  };
  message: string;
}
export interface CommandHostHealthCheck {
  id: string;
  name: string;
  role: string;
  url: string;
  reachable: boolean;
  status: "ok" | "degraded" | "down";
  httpStatus?: number;
  latencyMs?: number;
  message: string;
}
export interface CommandHostHealthSignal {
  id: "host-health";
  name: string;
  source: string;
  status: "ok" | "degraded" | "down";
  total: number;
  ok: number;
  degraded: number;
  down: number;
  checks: CommandHostHealthCheck[];
}
export interface CommandInfra {
  orchestrator: CommandInfraProbe;
  n8n: CommandInfraProbe;
  forgejo: CommandForgejoSignal;
  simoCi: CommandSimoCiHeadSignal;
  kuma: CommandKumaSignal;
  backupRestore: CommandBackupRestoreSignal;
  hostHealth: CommandHostHealthSignal;
  github: { id: string; name: string; configured: boolean; reachable: boolean };
  tailnet: CommandTailnetInventory;
}

export type LearningProposalKind = "memory" | "skill" | "policy" | "project_truth";
export type LearningProposalStatus = "pending" | "applied" | "rejected";
export type MemorySpace = "general" | "private" | "business" | "project" | "people" | "decision" | "evidence" | "temporary";
export type MemoryKind = "fact" | "preference" | "decision" | "task" | "reflection" | "source" | "context";
export type MemorySensitivity = "normal" | "private" | "sensitive" | "secret";
export type MemoryInjectPolicy = "never" | "manual" | "auto_profile";
export type MemoryRetention = "durable" | "temporary";
export type MemoryReviewStatus = "pending" | "approved" | "rejected";
export interface MemoryMetaEntry {
  key: string;
  updatedAt: string;
  space: MemorySpace;
  kind: MemoryKind;
  sensitivity: MemorySensitivity;
  injectPolicy: MemoryInjectPolicy;
  retention: MemoryRetention;
  reviewStatus: MemoryReviewStatus;
  source: string | null;
  expiresAt: string | null;
  embeddingStatus: "current" | "stale" | "missing";
}
export interface BrainDecisionLogItem {
  id: number;
  timestamp: string;
  actor: "user" | "jarvis" | "system";
  actionId: string | null;
  result: "ok" | "blocked" | "draft" | "pending" | "error";
  summary: string;
  details: {
    decisionType?: string;
    channel?: string;
    conversationId?: string;
    roleId?: string;
    space?: string;
    runId?: string;
    requestId?: string;
    reason?: string;
    key?: string;
    injectedKeys?: string[];
    injectedCount?: number;
    candidateCount?: number;
    requestedKeyPresent?: boolean;
  };
}
export interface LearningProposalView {
  id: string;
  kind: LearningProposalKind;
  targetKey: string;
  proposedValue: string;
  source: string;
  reason: string;
  evidenceJson: string;
  status: LearningProposalStatus;
  createdAt: string;
  decidedAt: string | null;
  decidedBy: string | null;
}

export interface LearningProposalMemoryReview {
  space?: MemorySpace;
  kind?: MemoryKind;
  sensitivity?: MemorySensitivity;
  injectPolicy?: MemoryInjectPolicy;
  retention?: MemoryRetention;
  expiresAt?: string | null;
}

// ── SIMO-AGENT-ROOM-LIVE-1 — Agent Room: Live-Projektion + Controls ──────────

export type AgentRoomLane = "ACTIVE" | "QUEUED_READY" | "WAIT_OWNER" | "WAIT_EXTERNAL" | "PARKED" | "DIAGNOSTICS" | "DONE";
export type AgentRoomItemStateName =
  | "READY" | "RUNNING" | "TROUBLESHOOTING" | "VERIFYING" | "WAIT_OWNER" | "WAIT_EXTERNAL" | "PARKED" | "PROVEN" | "FAILED_TERMINAL";
export type AgentRoomRecoveryStateName =
  | "OBSERVE" | "REPRODUCE" | "LOCALIZE" | "HYPOTHESIZE" | "CHALLENGE" | "FIX" | "VERIFY" | "RESUME";

export interface AgentRoomWorkitem {
  workitemId: string;
  missionId: string | null;
  parentWorkitemId: string | null;
  source: string;
  lane: AgentRoomLane;
  roomState: AgentRoomItemStateName;
  /** EMBER_CALM F: kanonische Feinachse (078) — Owner-Sprache statt Legacy-Map. */
  queueState?: string | null;
  queueStateReason?: string | null;
  kind: "OWNER_WORK" | "DIAGNOSTIC";
  waitingReason: string | null;
  /** FALSE-OWNER-GATE-1: kanonisch bewiesener Owner-Gate (nur bei active=true OWNER-Buttons). */
  ownerGate: { active: boolean; gateClass: string | null };
  queueStatus: string;
  priority: "urgent" | "high" | "normal" | "low";
  title: string;
  goal: string;
  reason: string | null;
  assignedAgent: string;
  writerLease: { leaseExpiresAt: string | null; attemptCount: number; maxAttempts: number; stopRequested: boolean };
  modelCapability: string | null;
  providerRoute: string | null;
  startedAt: string | null;
  heartbeatAt: string | null;
  recoveryState: AgentRoomRecoveryStateName | null;
  diagnosticStep: number;
  nextAction: string | null;
  blocker: string | null;
  /** GAM-01 §8 — Progress-/Degenerations-Visibility (STALLED/DEGRADED/ROTATING_CONTEXT/…).
   *  Optional: Server vor Migration 088 liefern die Felder nicht (Altleser-sicher). */
  progressState?: string | null;
  currentActor?: string | null;
  currentPhase?: string | null;
  lastMeaningfulProgressAt?: string | null;
  waitReasonDetail?: string | null;
  expectedNextAction?: string | null;
  rotationCount?: number;
  degenerationCount?: number;
  evidence: unknown[];
  failureHistory: Array<{
    fingerprint: string;
    errorClass: string;
    action: string;
    ts: string;
    diagnosticStep: number;
    disposition: string;
  }>;
  isRecoveryItem: boolean;
  isChallengeItem: boolean;
  recoveredFromWorkitemId: string | null;
  runs: AgentRoomRun[];
}

export interface AgentRoomRun {
  runId: string;
  parentRunId: string | null;
  role: string;
  modelCapability: string | null;
  resolvedModel: string | null;
  effort: "low" | "high" | null;
  provider: string;
  surface: string;
  state: "RUNNING" | "DONE" | "ERROR" | "AWAITING_APPROVAL" | "UNKNOWN";
  startedAt: string | null;
  updatedAt: string | null;
  heartbeatAt: string | null;
  turnCount: number;
  currentStage: string | null;
  contextUsage: { measured: false; note: string } | null;
  evidenceEventIds: string[];
  errorSummary: string | null;
  errorFingerprint: string | null;
}

export interface AgentRoomGlobal {
  openWorkitems: number;
  activeWorkitems: number;
  troubleshooting: number;
  waitOwner: number;
  waitExternal: number;
  parked: number;
  recoveryItemsOpen: number;
  budgetState: { route: string; pool: string; window5h: string; weekly: string; note: string };
  slots: { primaryInUse: number; maxPrimarySlots: number | null; availablePrimary: number | null; reviewInUse: number; note: string };
  autoExecutor: { configured: boolean; running: boolean; lastTickAt: string | null; lastTickStatus: string | null };
  providerHealth: Array<{ surface: string; status: "up" | "unknown"; detail: string }>;
}

export interface AgentRoomState {
  ok: boolean;
  schema: string;
  generatedAt: string;
  global: AgentRoomGlobal;
  lanes: Record<AgentRoomLane, AgentRoomWorkitem[]>;
}

export interface AgentRoomActionPayload {
  type: string;
  workitemId?: string;
  goal?: string;
  title?: string;
  priority?: string;
  claim?: string;
  decision?: "approve" | "reject";
  note?: string;
  reason?: string;
  capability?: "glm.fast" | "glm.default" | "glm.reasoning";
  errorClass?: string;
  action?: string;
  summary?: string;
  stateHash?: string;
  provider?: string;
  model?: string;
  surface?: string;
  configRevision?: string;
}

export async function fetchAgentRoomState(): Promise<AgentRoomState> {
  return jfetch<AgentRoomState>("/api/agent-room/state");
}

export async function fetchAgentRoomWorkitem(id: string): Promise<{ ok: boolean; workitem: AgentRoomWorkitem }> {
  return jfetch<{ ok: boolean; workitem: AgentRoomWorkitem }>(`/api/agent-room/workitem/${encodeURIComponent(id)}`);
}

export async function postAgentRoomAction(payload: AgentRoomActionPayload): Promise<{ ok: boolean; action?: string; workitemId?: string; detail?: Record<string, unknown>; error?: string }> {
  return jfetch<{ ok: boolean; action?: string; workitemId?: string; detail?: Record<string, unknown>; error?: string }>("/api/agent-room/actions", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}
